import { Module } from '../../types';

export const dspyModule3: Module = {
  id: 'dspy-module3',
  title: 'Evaluation in DSPy',
  description:
    'Learn to build development sets, define metrics, and run comprehensive evaluations to measure and improve your DSPy programs.',
  duration: '3-4 hours',
  content: `# Module 3: Evaluation in DSPy

## Module Overview
Evaluation is the cornerstone of building high-quality AI systems in DSPy. This module teaches you how to systematically measure and improve your programs through data collection, metric definition, and iterative evaluation. You'll learn to think like a machine learning engineer, using data-driven approaches to enhance your AI applications.

## 🎯 Learning Objectives

By the end of this module, you will:
- Build effective development and test datasets for your tasks
- Define meaningful metrics that capture what makes outputs good
- Use DSPy's evaluation tools to measure program performance
- Implement AI-powered metrics for complex evaluation scenarios
- Apply evaluation-driven development practices

## 📊 Building Development Sets

### The Role of Data in DSPy

In DSPy, we distinguish between three types of data:
1. **Training data**: Used by optimizers to improve prompts/demonstrations
2. **Development data**: Used to evaluate and iterate on your programs
3. **Test data**: Final held-out evaluation

\`\`\`python
# DSPy's data philosophy
# - You need FAR less data than traditional ML (20-200 examples go a long way)
# - You rarely need labels for intermediate steps
# - Focus on input diversity and output quality
\`\`\`

### Understanding DSPy Examples

The core data type in DSPy is \`Example\`:

\`\`\`python
import dspy

# Basic Example creation
qa_example = dspy.Example(
    question="What is the capital of France?",
    answer="Paris"
)

# Access fields
print(qa_example.question)  # "What is the capital of France?"
print(qa_example.answer)    # "Paris"

# Examples are like dicts with extra utilities
print(qa_example)
# Example({'question': 'What is the capital of France?', 'answer': 'Paris'}) (input_keys=None)
\`\`\`

### Specifying Input Keys: The Foundation of Effective Training

Input keys are one of DSPy's most important concepts, yet they're often overlooked by beginners. Think of input keys as the contract between your data and DSPy's optimization algorithms - they tell DSPy exactly what information your program receives vs. what it should produce.

#### What Are Input Keys?

Input keys define which fields in your examples represent the **information available at inference time** (inputs) versus the **expected outputs** (labels/targets). This distinction is crucial because:

- **Inputs**: What your program will receive when deployed (questions, documents, user queries)
- **Labels**: What your program should learn to produce (answers, classifications, summaries)

#### Why Input Keys Matter

Without proper input key specification, DSPy's optimizers make incorrect assumptions about your data:

1. **Training Confusion**: Optimizers might try to "use" the answer field during training, leading to data leakage
2. **Evaluation Errors**: Metrics can't properly separate prediction inputs from expected outputs
3. **Deployment Failures**: Your program expects different data structure than what's available in production

#### Practical Input Key Usage

\`\`\`python
# Mark inputs explicitly - tells DSPy what the program receives
qa_example = dspy.Example(
    question="What is the capital of France?",
    answer="Paris"  # This becomes the label/target
).with_inputs("question")

# Multiple inputs for complex scenarios
doc_qa_example = dspy.Example(
    context="France is a country in Europe. Its capital is Paris.",
    question="What is the capital of France?", 
    answer="Paris"
).with_inputs("context", "question")  # Both are available at inference

# Classification with metadata
review_example = dspy.Example(
    review_text="This product is amazing!",
    product_category="electronics",
    sentiment="positive",
    confidence=0.95
).with_inputs("review_text", "product_category")  # sentiment & confidence are targets

# Access inputs and labels separately for validation
print(doc_qa_example.inputs())   # Only input fields
print(doc_qa_example.labels())   # Only non-input fields
\`\`\`

#### Common Input Key Patterns

Different tasks require different input specifications:

\`\`\`python
# Question Answering: question → answer
qa_example.with_inputs("question")

# Document QA: context + question → answer  
doc_qa_example.with_inputs("context", "question")

# Classification: text → category
classification_example.with_inputs("text")

# Summarization: document → summary
summary_example.with_inputs("document")

# Multi-step reasoning: context + query → reasoning + answer
reasoning_example.with_inputs("context", "query")  # reasoning & answer are outputs
\`\`\`

#### Input Keys Best Practices

1. **Be Explicit**: Always specify input keys, even if it seems obvious
2. **Match Production**: Input keys should reflect exactly what data is available during deployment
3. **Validate Early**: Use .inputs() and .labels() methods to verify your specification
4. **Document Assumptions**: Make it clear what information your program expects vs. produces

Remember: Input keys aren't just metadata - they're the foundation that enables DSPy's optimization algorithms to work correctly. Get them right, and everything else follows naturally.

### Building Datasets from Various Sources

Real-world data comes in many formats, and your ability to quickly transform diverse data sources into DSPy Examples directly impacts your productivity. This section covers practical patterns for working with the most common data formats you'll encounter in production environments.

#### From Lists and Dicts

The simplest and most common starting point is Python's native data structures. This approach is perfect for prototyping, testing, or when you're programmatically generating data. The key insight is that DSPy Examples can be created from any dictionary-like structure, making data transformation straightforward:

\`\`\`python
# From raw data - often how you start during development
raw_data = [
    {"question": "What is 2+2?", "answer": "4"},
    {"question": "Capital of Japan?", "answer": "Tokyo"},
    # ... more examples
]

# Convert to DSPy Examples using dictionary unpacking
# The ** operator passes dict keys as keyword arguments
dataset = [
    dspy.Example(**d).with_inputs("question") 
    for d in raw_data
]

# Alternative: More explicit construction for complex transformations
dataset = []
for item in raw_data:
    # You can transform or validate data here
    example = dspy.Example(
        question=item['question'].strip(),  # Clean whitespace
        answer=item['answer'].strip()
    ).with_inputs("question")
    dataset.append(example)
\`\`\`

#### From CSV/JSON Files

Most evaluation datasets are stored as files. CSV excels for tabular data with consistent columns, while JSON handles nested structures and varying fields. Here's how to efficiently load both formats while handling common real-world issues:

\`\`\`python
import pandas as pd
import json

# From CSV - leveraging pandas for robust parsing
df = pd.read_csv("qa_data.csv")

# Handle common CSV issues
df = df.dropna(subset=['question', 'answer'])  # Remove rows with missing data
df['question'] = df['question'].str.strip()     # Clean whitespace
df['answer'] = df['answer'].str.strip()

# Convert to DSPy format with validation
dataset = []
for _, row in df.iterrows():
    # Skip invalid entries
    if len(row['question']) < 3 or len(row['answer']) < 1:
        continue
        
    dataset.append(
        dspy.Example(
            question=row['question'],
            answer=row['answer']
        ).with_inputs("question")
    )

print(f"Loaded {len(dataset)} valid examples from CSV")

# From JSON - supporting various structures
with open("qa_data.json") as f:
    data = json.load(f)
    
    # Handle different JSON structures gracefully
    if isinstance(data, dict) and 'examples' in data:
        # Nested structure: {"examples": [...]}
        examples = data['examples']
    elif isinstance(data, list):
        # Direct array of examples
        examples = data
    else:
        raise ValueError("Unexpected JSON structure")
    
    dataset = [
        dspy.Example(**item).with_inputs("question")
        for item in examples
        if 'question' in item and 'answer' in item  # Validate required fields
    ]
\`\`\`

#### From HuggingFace Datasets

HuggingFace has become the de facto standard for sharing research datasets. Their datasets library provides consistent access to thousands of datasets, but each has its own schema. Here's how to work with them effectively:

\`\`\`python
from datasets import load_dataset

# Load a subset for initial development (full datasets can be huge)
hf_dataset = load_dataset("squad", split="validation[:100]")

# Examine the schema first - critical for proper field mapping
print("Dataset features:", hf_dataset.features)
print("First example:", hf_dataset[0])

# Convert to DSPy format with careful field mapping
dataset = []
for ex in hf_dataset:
    # Handle SQuAD's specific structure where answers are lists
    answer_text = ex['answers']['text'][0] if ex['answers']['text'] else ""
    
    # Create example with proper input specification
    example = dspy.Example(
        context=ex['context'],
        question=ex['question'],
        answer=answer_text,
        # Include metadata for analysis
        id=ex['id'],
        answer_start=ex['answers']['answer_start'][0] if ex['answers']['answer_start'] else -1
    ).with_inputs("context", "question")  # Both context and question are inputs
    
    dataset.append(example)

# For other popular datasets, adjust field mappings:
# - Natural Questions: 'question_text', 'document_text', 'long_answer'
# - MS MARCO: 'query', 'passages', 'answers'
# - MultiNLI: 'premise', 'hypothesis', 'label'
\`\`\`

### Data Collection Strategies

Building high-quality datasets for DSPy doesn't require massive data collection efforts. Instead, it's about being strategic with the data you gather. These proven strategies help you bootstrap from zero to a working system efficiently.

#### 1. Start Small, Iterate Fast

The biggest misconception in DSPy is that you need hundreds or thousands of examples to get started. In reality, even 20 well-chosen examples can produce surprisingly good results. The key is to start with high-quality, diverse examples that represent your problem space well:

\`\`\`python
# Even 20 examples can be useful! Quality > Quantity in DSPy
initial_examples = [
    dspy.Example(
        text="The new iPhone has amazing camera features",
        sentiment="positive"
    ).with_inputs("text"),
    dspy.Example(
        text="This product broke after one day",
        sentiment="negative"
    ).with_inputs("text"),
    # ... 18 more examples covering edge cases
]

# Strategic example selection principles:
# 1. Cover all output categories (positive, negative, neutral)
# 2. Include edge cases (sarcasm, mixed sentiment)
# 3. Vary input lengths and styles
# 4. Represent your production data distribution

# Use these initial examples to bootstrap your system:
# Step 1: Test your initial program architecture
baseline = dspy.Predict("text -> sentiment")
for ex in initial_examples[:5]:
    pred = baseline(text=ex.text)
    print(f"Input: {ex.text}")
    print(f"Predicted: {pred.sentiment}, Expected: {ex.sentiment}")

# Step 2: Define and refine your evaluation metric
def sentiment_metric(example, pred, trace=None):
    return example.sentiment.lower() == pred.sentiment.lower()

# Step 3: Run your first optimization
optimizer = dspy.BootstrapFewShot(metric=sentiment_metric)
optimized = optimizer.compile(baseline, trainset=initial_examples)

# The cycle: Collect → Test → Evaluate → Optimize → Repeat
\`\`\`

#### 2. Bootstrapping More Data

Once you have a working system, you can use it to generate more training data. This bootstrapping approach is particularly powerful in DSPy because the framework can learn from synthetic examples. The key is maintaining quality through strategic generation:

\`\`\`python
class DataGenerator(dspy.Module):
    """Generate synthetic training examples with quality control."""
    
    def __init__(self):
        # Use chain-of-thought for higher quality generation
        self.generate = dspy.ChainOfThought(
            "topic -> question, answer"
        )
        
        # Add a validator to ensure quality
        self.validate = dspy.Predict(
            "question, answer -> is_valid: bool, reason: str"
        )
    
    def forward(self, topic):
        # Generate with reasoning for better quality
        result = self.generate(topic=topic)
        
        # Validate the generated example
        validation = self.validate(
            question=result.question,
            answer=result.answer
        )
        
        # Only return valid examples
        if validation.is_valid:
            return result
        else:
            # Retry or log failed generation
            print(f"Invalid generation for {topic}: {validation.reason}")
            return None

# Strategic topic selection for comprehensive coverage
generator = DataGenerator()
topics = [
    "science",      # Factual, objective answers
    "history",      # Dates, events, causation
    "technology",   # Current, evolving topics
    "sports",       # Statistics, rules, events
    "philosophy",   # Abstract, reasoning-heavy
    "mathematics",  # Precise, calculable answers
]

# Generate with quality control
synthetic_data = []
for topic in topics:
    attempts = 0
    successful = 0
    
    # Generate multiple examples per topic with retry logic
    while successful < 5 and attempts < 10:
        result = generator(topic=topic)
        attempts += 1
        
        if result:
            synthetic_data.append(
                dspy.Example(
                    question=result.question,
                    answer=result.answer,
                    source="synthetic",  # Track data provenance
                    topic=topic
                ).with_inputs("question")
            )
            successful += 1
    
    print(f"Topic '{topic}': Generated {successful} examples in {attempts} attempts")

# Combine with real data for best results
combined_dataset = initial_examples + synthetic_data
print(f"Total dataset size: {len(combined_dataset)} ({len(initial_examples)} real, {len(synthetic_data)} synthetic)")
\`\`\`

#### 3. Active Learning Approach

Active learning maximizes the value of human annotation effort by focusing on the examples where your system is most uncertain. This approach is particularly effective when you have access to unlabeled data and limited annotation budget:

\`\`\`python
# Start with unlabeled data from your domain
unlabeled_questions = [
    "How does photosynthesis work?",
    "What caused the French Revolution?",
    "Why is the sky blue?",
    "How do vaccines prevent disease?",
    # ... hundreds more from your application logs or domain
]

# Use your current best program to generate labels
current_program = dspy.ChainOfThought("question -> answer")

# Implement uncertainty-aware active learning
labeled_data = []
uncertain_examples = []

for question in unlabeled_questions:
    # Generate answer with reasoning
    pred = current_program(question=question)
    
    # Estimate uncertainty (simple heuristic based on answer length/complexity)
    answer_length = len(pred.answer.split())
    has_reasoning = hasattr(pred, 'reasoning') and len(pred.reasoning) > 50
    
    # High-confidence examples can be auto-labeled
    if answer_length > 10 and has_reasoning:
        confidence = "high"
    elif answer_length > 5:
        confidence = "medium"
    else:
        confidence = "low"
    
    # Prioritize human review for uncertain cases
    if confidence == "low":
        uncertain_examples.append((question, pred.answer))
    elif confidence == "high":
        # Auto-accept high-confidence predictions
        labeled_data.append(
            dspy.Example(
                question=question,
                answer=pred.answer,
                confidence=confidence,
                auto_labeled=True
            ).with_inputs("question")
        )

# Efficient human review for uncertain cases
print(f"\\nNeed human review for {len(uncertain_examples)} uncertain examples:")
for i, (question, predicted_answer) in enumerate(uncertain_examples[:10]):  # Review top 10
    print(f"\\n[{i+1}/10] Question: {question}")
    print(f"Predicted Answer: {predicted_answer}")
    
    # In production, this would be a proper annotation interface
    feedback = input("Correct? (y/n/skip/edit): ").lower()
    
    if feedback == 'y':
        labeled_data.append(
            dspy.Example(
                question=question,
                answer=predicted_answer,
                human_verified=True
            ).with_inputs("question")
        )
    elif feedback == 'edit':
        corrected = input("Provide correct answer: ")
        labeled_data.append(
            dspy.Example(
                question=question,
                answer=corrected,
                human_corrected=True
            ).with_inputs("question")
        )
    # 'n' or 'skip' - don't add to dataset

print(f"\\nActive learning complete: {len(labeled_data)} high-quality examples collected")
\`\`\`

### Dataset Splitting Best Practices: The Foundation of Reliable Evaluation

Dataset splitting is where many AI projects succeed or fail, yet it's often treated as an afterthought. In DSPy, proper dataset splitting is crucial because the framework's optimization algorithms need clean separation between training data (for prompt optimization), development data (for iterative improvement), and test data (for final evaluation).

Think of dataset splitting like conducting a scientific experiment: you need a clean control group (test set) that remains untouched until the very end, a working group (development set) for iterative experiments, and training materials (training set) for the initial learning. Contamination between these groups leads to overly optimistic results that don't hold up in production.

#### The Psychology of Data Splits

Unlike traditional machine learning where you might have thousands of examples, DSPy often works with smaller datasets (20-200 examples). This scarcity makes every example precious and splitting decisions critical. Here's how to think about each split:

**Training Set**: Your DSPy optimizer's raw material. These examples teach the system how to transform inputs into outputs through prompt engineering and demonstration selection. Too few examples and optimization fails; too many and you starve your evaluation sets.

**Development Set**: Your iteration sandbox. This is where you experiment with different metrics, test various approaches, and measure progress. You'll look at this data hundreds of times during development - that's exactly what it's for.

**Test Set**: Your reality check. This data represents the real world your system will face. Touch it only once, at the very end, to get an honest assessment of production performance.

#### Smart Splitting Strategies for Different Dataset Sizes

DSPy's unique characteristics require adapted splitting strategies:

\`\`\`python
import random
from collections import defaultdict

# Load your full dataset
all_data = [...]  # Your examples

# CRITICAL: Set random seed for reproducible splits
random.Random(42).shuffle(all_data)

# Adaptive splitting based on dataset characteristics
def smart_split(data, min_train=20, min_dev=10, min_test=10):
    """
    Intelligent dataset splitting that adapts to DSPy's needs.
    
    DSPy can work with minimal training data but needs sufficient 
    development data for reliable metric evaluation.
    """
    total = len(data)
    
    if total < 50:
        # Micro dataset: Prioritize development and test sets
        train_size = max(min_train, total // 4)
        remaining = total - train_size
        dev_size = remaining // 2
        test_size = remaining - dev_size
        
        print(f"Micro dataset strategy: Minimal training, balanced dev/test")
        
    elif total < 200:
        # Small dataset: Balanced approach with slight dev bias
        train_size = max(min_train, int(total * 0.4))
        dev_size = max(min_dev, int(total * 0.35))
        test_size = total - train_size - dev_size
        
        print(f"Small dataset strategy: Conservative training, robust evaluation")
        
    else:
        # Standard dataset: Traditional splits work well
        train_size = int(total * 0.6)
        dev_size = int(total * 0.2) 
        test_size = total - train_size - dev_size
        
        print(f"Standard dataset strategy: Traditional 60/20/20 split")
    
    return train_size, dev_size, test_size

# Apply smart splitting
train_size, dev_size, test_size = smart_split(all_data)

trainset = all_data[:train_size]
devset = all_data[train_size:train_size + dev_size]
testset = all_data[train_size + dev_size:]

print(f"Final split - Train: {len(trainset)}, Dev: {len(devset)}, Test: {len(testset)}")
\`\`\`

#### Stratified Splitting: Preserving Data Characteristics

When your dataset has important characteristics (question types, difficulty levels, domains), you want each split to be representative:

\`\`\`python
def stratified_split(data, group_key="question_type", train_ratio=0.6, dev_ratio=0.2):
    """
    Ensure each split maintains the same distribution of data characteristics.
    Critical for fair evaluation across different question types or domains.
    """
    # Group examples by characteristic
    groups = defaultdict(list)
    for example in data:
        group = getattr(example, group_key, "unknown")
        groups[group].append(example)
    
    trainset, devset, testset = [], [], []
    
    for group_name, group_examples in groups.items():
        # Shuffle within group
        random.Random(42).shuffle(group_examples)
        
        # Calculate splits for this group
        n = len(group_examples)
        train_n = int(n * train_ratio)
        dev_n = int(n * dev_ratio)
        
        # Split group examples
        group_train = group_examples[:train_n]
        group_dev = group_examples[train_n:train_n + dev_n]
        group_test = group_examples[train_n + dev_n:]
        
        trainset.extend(group_train)
        devset.extend(group_dev)
        testset.extend(group_test)
        
        print(f"Group '{group_name}': {len(group_train)} train, {len(group_dev)} dev, {len(group_test)} test")
    
    # Final shuffle to mix groups
    random.Random(42).shuffle(trainset)
    random.Random(42).shuffle(devset)
    random.Random(42).shuffle(testset)
    
    return trainset, devset, testset

# Use stratified splitting for balanced evaluation
# trainset, devset, testset = stratified_split(all_data, group_key="question_type")
\`\`\`

#### Time-Aware Splitting: Simulating Production Reality

If your data has temporal characteristics (recent news, evolving topics, seasonal patterns), consider time-aware splits that better simulate production conditions:

\`\`\`python
def temporal_split(data, date_key="created_date", split_date=None):
    """
    Split data chronologically to simulate real-world deployment.
    
    Train on older data, validate on recent data, test on the most recent.
    This mirrors how your system will actually be used in production.
    """
    if split_date is None:
        # Use most recent 20% for test, next 20% for dev
        sorted_data = sorted(data, key=lambda x: getattr(x, date_key))
        total = len(sorted_data)
        
        train_end = int(total * 0.6)
        dev_end = int(total * 0.8)
        
        trainset = sorted_data[:train_end]
        devset = sorted_data[train_end:dev_end]
        testset = sorted_data[dev_end:]
        
        print(f"Temporal split: Training on oldest {len(trainset)} examples")
        print(f"               Validating on middle {len(devset)} examples")
        print(f"               Testing on newest {len(testset)} examples")
        
        return trainset, devset, testset
    
    # Custom date-based splitting logic here
    pass

# For time-sensitive data
# trainset, devset, testset = temporal_split(all_data, date_key="created_date")
\`\`\`

#### Split Validation and Quality Checks

After splitting, always validate that your splits make sense:

\`\`\`python
def validate_splits(trainset, devset, testset):
    """
    Comprehensive validation of dataset splits.
    Catches common issues before they become expensive mistakes.
    """
    print("🔍 Validating dataset splits...")
    
    # 1. Size validation
    total = len(trainset) + len(devset) + len(testset)
    print(f"📊 Split sizes: Train={len(trainset)} ({len(trainset)/total:.1%}), "
          f"Dev={len(devset)} ({len(devset)/total:.1%}), "
          f"Test={len(testset)} ({len(testset)/total:.1%})")
    
    # 2. Minimum size warnings
    if len(trainset) < 20:
        print("⚠️  Training set is very small - DSPy optimization may struggle")
    if len(devset) < 10:
        print("⚠️  Development set is very small - evaluation may be unreliable")
    if len(testset) < 5:
        print("⚠️  Test set is very small - final results may not be trustworthy")
    
    # 3. Check for data leakage (simplified)
    train_questions = {ex.question for ex in trainset if hasattr(ex, 'question')}
    dev_questions = {ex.question for ex in devset if hasattr(ex, 'question')}
    test_questions = {ex.question for ex in testset if hasattr(ex, 'question')}
    
    overlap_train_dev = train_questions & dev_questions
    overlap_train_test = train_questions & test_questions
    overlap_dev_test = dev_questions & test_questions
    
    if overlap_train_dev:
        print(f"🚨 DATA LEAKAGE: {len(overlap_train_dev)} questions appear in both train and dev")
    if overlap_train_test:
        print(f"🚨 DATA LEAKAGE: {len(overlap_train_test)} questions appear in both train and test")
    if overlap_dev_test:
        print(f"🚨 DATA LEAKAGE: {len(overlap_dev_test)} questions appear in both dev and test")
    
    # 4. Distribution checks (if question types available)
    def check_distribution(split_name, split_data):
        if hasattr(split_data[0], 'question_type'):
            types = defaultdict(int)
            for ex in split_data:
                types[ex.question_type] += 1
            print(f"📈 {split_name} distribution: {dict(types)}")
    
    if trainset: check_distribution("Train", trainset)
    if devset: check_distribution("Dev", devset)
    if testset: check_distribution("Test", testset)
    
    print("✅ Split validation complete!")

# Always validate your splits
validate_splits(trainset, devset, testset)
\`\`\`

#### The Golden Rules of DSPy Dataset Splitting

1. **Never touch your test set** until final evaluation. Treat it like a sealed envelope.

2. **Prioritize development set quality** over training set size. DSPy can optimize with few examples, but needs good dev data for reliable metrics.

3. **Document your splitting decisions**. Future you (and your team) will thank you for explaining why you chose specific ratios.

4. **Use stratified splits** when data has important subgroups. Random splits can accidentally create unrepresentative sets.

5. **Validate splits before starting work**. Catching data leakage early saves weeks of invalid results.

6. **Consider production deployment patterns** when designing splits. Time-aware splits often reflect real-world usage better than random splits.

Remember: In DSPy, your dataset split strategy directly impacts the reliability of your optimization and evaluation processes. Spend time getting this right - it's the foundation everything else builds upon.

### Working with Complex Data Types

Real-world data is rarely as simple as question-answer pairs. You'll often deal with nested structures, multiple related fields, and complex relationships between data elements. DSPy seamlessly handles complex data types through Python's type system, enabling you to work with rich, structured data while maintaining type safety and clarity.

The key insight is that DSPy Examples can contain any Python object, including custom classes, dataclasses, and Pydantic models. This flexibility allows you to model your domain accurately without compromising on data structure:

\`\`\`python
from typing import List, Dict, Optional
from pydantic import BaseModel, Field
from datetime import datetime
from enum import Enum

# Define domain-specific enumerations for type safety
class ProductCategory(str, Enum):
    ELECTRONICS = "electronics"
    CLOTHING = "clothing"
    HOME = "home"
    FOOD = "food"

# Model complex nested structures with Pydantic
class Product(BaseModel):
    """Rich product model with validation and documentation."""
    name: str = Field(..., description="Product display name")
    price: float = Field(..., gt=0, description="Price in USD")
    features: List[str] = Field(default_factory=list)
    category: ProductCategory
    in_stock: bool = True
    
    # Pydantic provides automatic validation
    class Config:
        validate_assignment = True

class Customer(BaseModel):
    """Customer information for personalized analysis."""
    id: str
    purchase_history: List[str] = Field(default_factory=list)
    preferred_categories: List[ProductCategory] = Field(default_factory=list)

class Review(BaseModel):
    """Complete review with all context needed for analysis."""
    product: Product
    customer: Customer
    rating: int = Field(..., ge=1, le=5)
    text: str = Field(..., min_length=10)
    verified_purchase: bool
    helpful_votes: int = 0
    date: datetime = Field(default_factory=datetime.now)
    
    # Computed properties for additional context
    @property
    def is_positive(self) -> bool:
        return self.rating >= 4
    
    @property
    def review_age_days(self) -> int:
        return (datetime.now() - self.date).days

# Create rich examples that capture full business context
example = dspy.Example(
    # Complex nested input structure
    review=Review(
        product=Product(
            name="Laptop Pro M2",
            price=1299.99,
            features=["16GB RAM", "512GB SSD", "M2 Chip", "14-inch display"],
            category=ProductCategory.ELECTRONICS
        ),
        customer=Customer(
            id="cust_123",
            purchase_history=["laptop_2021", "mouse_2022"],
            preferred_categories=[ProductCategory.ELECTRONICS]
        ),
        rating=5,
        text="Amazing performance and battery life! The M2 chip handles everything I throw at it.",
        verified_purchase=True,
        helpful_votes=42
    ),
    # Multiple output fields for comprehensive analysis
    summary="Highly satisfied with performance and battery",
    sentiment="positive",
    key_features_mentioned=["performance", "battery life", "M2 chip"],
    recommendation_score=0.95
).with_inputs("review")  # Only review is input, rest are labels

# Working with batches of complex data
batch_examples = []
for review_data in complex_dataset:
    # Validation happens automatically with Pydantic
    try:
        review = Review(**review_data)
        
        # Process based on business logic
        if review.is_positive and review.verified_purchase:
            example = dspy.Example(
                review=review,
                # Generate labels using your existing system or annotations
                summary=generate_summary(review),
                sentiment="positive" if review.rating >= 4 else "negative",
                should_feature=review.helpful_votes > 20
            ).with_inputs("review")
            
            batch_examples.append(example)
    except ValidationError as e:
        # Pydantic validation ensures data quality
        print(f"Skipping invalid review: {e}")

# Advanced: Custom serialization for specific use cases
class ReviewAnalysisTask(BaseModel):
    """Encapsulate the entire analysis task with all context."""
    review: Review
    competitor_products: List[Product]
    market_context: Dict[str, Any]
    
    def to_dspy_example(self, labels: Dict[str, Any]) -> dspy.Example:
        """Convert complex task to DSPy Example with proper structure."""
        return dspy.Example(
            # Flatten complex inputs as needed
            review_text=self.review.text,
            product_name=self.review.product.name,
            product_features=", ".join(self.review.product.features),
            rating=self.review.rating,
            market_position=self.market_context.get("position", "unknown"),
            # Add all labels
            **labels
        ).with_inputs("review_text", "product_name", "product_features", "rating", "market_position")

# This approach scales to any complexity level your domain requires
\`\`\`

## 📏 Defining Metrics: The Art and Science of Evaluation

Metrics are the bridge between your task requirements and DSPy's optimization capabilities. They transform subjective quality assessments into quantifiable signals that guide automatic prompt engineering. The metrics you choose directly determine what your system learns to optimize for - choose wisely.

### What Makes a Good Metric?

A metric is fundamentally a function that quantifies output quality, but designing effective metrics requires balancing multiple considerations. Think of metrics as teaching signals - they tell DSPy's optimizers what patterns to reinforce and what outputs to avoid.

The anatomy of a DSPy metric follows a consistent pattern:

\`\`\`python
def metric(example, prediction, trace=None):
    """
    The standard DSPy metric signature.
    
    Args:
        example: The gold Example with inputs and expected outputs
        prediction: The actual output from your program
        trace: Optional trace of module calls (used by advanced optimizers)
    
    Returns:
        score: A number (float, int, or bool) indicating quality
               - Bool: Pass/fail metrics (True = 1.0, False = 0.0)
               - Float: Continuous scores (typically 0.0 to 1.0)
               - Int: Discrete scores (useful for ranking)
    """
    # Your evaluation logic here
    # Key principles:
    # 1. Be deterministic - same inputs should always give same score
    # 2. Be efficient - this runs on every example during optimization
    # 3. Be meaningful - the score should reflect real quality differences
    
    return score
\`\`\`

Great metrics share several characteristics:

**Task Alignment**: Measure what actually matters for your use case. A medical diagnosis system needs different metrics than a creative writing assistant.

**Robustness**: Handle edge cases gracefully. What if the prediction is empty? What if it's in an unexpected format?

**Gradient**: Provide signal for improvement. Binary pass/fail metrics give less optimization signal than continuous scores.

**Efficiency**: Run quickly on large datasets. Save expensive evaluations (like API calls) for final testing.

### Simple Metrics: The Foundation

Simple metrics often work surprisingly well and should be your starting point. They're easy to understand, debug, and often capture the core quality signal you need.

#### Exact Match

The strictest metric - perfect for tasks with canonical answers:

\`\`\`python
def exact_match(example, pred, trace=None):
    """
    Check if prediction exactly matches gold answer.
    
    Use cases:
    - Multiple choice questions
    - Entity extraction with standardized formats
    - Classification tasks
    - Math problems with unique answers
    """
    # Normalize both strings for fair comparison
    gold = example.answer.lower().strip()
    predicted = pred.answer.lower().strip()
    
    # Handle common variations
    gold = gold.replace("-", " ").replace("_", " ")
    predicted = predicted.replace("-", " ").replace("_", " ")
    
    return gold == predicted

# Enhanced version with multiple acceptable answers
def exact_match_flexible(example, pred, trace=None):
    """Allow multiple correct answers."""
    predicted = pred.answer.lower().strip()
    
    # Check against primary answer
    if predicted == example.answer.lower().strip():
        return True
    
    # Check against alternative answers if provided
    if hasattr(example, 'alternative_answers'):
        return predicted in [alt.lower().strip() for alt in example.alternative_answers]
    
    return False

# DSPy includes a built-in version
from dspy.evaluate.metrics import answer_exact_match
\`\`\`

#### Partial Match

More forgiving metrics for when exact matching is too strict:

\`\`\`python
def partial_match(example, pred, trace=None):
    """
    Check if gold answer appears in prediction or vice versa.
    
    Use cases:
    - Question answering where context is acceptable
    - Information extraction from longer text
    - Tasks where elaboration is okay
    """
    gold = example.answer.lower()
    predicted = pred.answer.lower()
    
    # Bidirectional check handles both under and over-generation
    return gold in predicted or predicted in gold

def keyword_overlap(example, pred, trace=None):
    """More sophisticated partial matching based on important terms."""
    import re
    
    # Extract meaningful words (simple tokenization)
    def get_keywords(text):
        # Remove punctuation and split
        words = re.findall(r'\\b\\w+\\b', text.lower())
        # Remove common stop words
        stop_words = {'the', 'is', 'at', 'which', 'on', 'a', 'an', 'as', 'are', 'was', 'were', 'be'}
        return {w for w in words if w not in stop_words and len(w) > 2}
    
    gold_keywords = get_keywords(example.answer)
    pred_keywords = get_keywords(pred.answer)
    
    if not gold_keywords:
        return 1.0  # Empty gold answer = any prediction is fine
    
    # Calculate Jaccard similarity
    intersection = gold_keywords & pred_keywords
    union = gold_keywords | pred_keywords
    
    return len(intersection) / len(union) if union else 0.0
\`\`\`

#### Numeric Metrics

Real-world applications often involve numbers that need special handling:

\`\`\`python
def numeric_accuracy(example, pred, trace=None):
    """
    For numeric answers, check if within acceptable tolerance.
    
    Handles:
    - Different number formats (1000 vs 1,000)
    - Percentage vs decimal (50% vs 0.5)
    - Units and currencies ($100 vs 100)
    - Scientific notation
    """
    import re
    
    def extract_number(text):
        """Extract the first number from text."""
        # Remove common formatting
        text = text.replace(',', '').replace('$', '').replace('%', '')
        
        # Find all numbers (including decimals and negatives)
        numbers = re.findall(r'-?\\d+\\.?\\d*', text)
        
        if not numbers:
            return None
            
        num = float(numbers[0])
        
        # Handle percentages
        if '%' in text and num > 1:
            num = num / 100
            
        return num
    
    try:
        gold_num = extract_number(example.answer)
        pred_num = extract_number(pred.answer)
        
        if gold_num is None or pred_num is None:
            return False
        
        # Adaptive tolerance based on magnitude
        if abs(gold_num) < 1:
            # Absolute tolerance for small numbers
            tolerance = 0.01
        else:
            # Relative tolerance for larger numbers
            tolerance = abs(gold_num) * 0.01  # 1% tolerance
            
        return abs(gold_num - pred_num) <= tolerance
        
    except (ValueError, TypeError, AttributeError):
        return False

def range_accuracy(example, pred, trace=None):
    """For answers that should fall within a range."""
    try:
        # Example format: "10-15" or "between 10 and 15"
        import re
        
        # Extract range from gold answer
        range_match = re.search(r'(\\d+)\\s*[-to]\\s*(\\d+)', example.answer)
        if range_match:
            min_val, max_val = map(float, range_match.groups())
        else:
            return False
        
        # Extract number from prediction
        pred_match = re.search(r'\\d+\\.?\\d*', pred.answer)
        if pred_match:
            pred_val = float(pred_match.group())
        else:
            return False
            
        return min_val <= pred_val <= max_val
        
    except (ValueError, AttributeError):
        return False
### Complex Metrics for Long-form Outputs

When your AI system generates longer, more nuanced outputs like summaries, explanations, or analyses, simple exact-match metrics fall short. Complex metrics evaluate multiple dimensions of quality simultaneously, providing richer optimization signals for sophisticated tasks.

#### Multi-criteria Evaluation

Real-world applications often have multiple quality requirements that must be balanced. Multi-criteria metrics let you encode these complex requirements while maintaining flexibility in how they're weighted:

\`\`\`python
def comprehensive_metric(example, pred, trace=None):
    """
    Evaluate multiple aspects of the output simultaneously.
    
    This pattern is powerful for:
    - Content that must satisfy multiple constraints
    - Tasks where quality has multiple dimensions
    - Balancing competing objectives
    """
    
    # Criterion 1: Factual correctness (most important)
    correct = example.answer.lower() in pred.answer.lower()
    
    # Criterion 2: Appropriate length (avoid too brief or too verbose)
    answer_length = len(pred.answer.split())
    if answer_length < 20:
        length_score = 0.0  # Too brief
    elif answer_length > 200:
        length_score = 0.5  # Too verbose
    else:
        length_score = 1.0  # Just right
    
    # Criterion 3: Proper formatting/structure
    has_structure = any(marker in pred.answer for marker in ['\n', '•', '-', '1.', '2.'])
    
    # Criterion 4: Professional tone (simple heuristic)
    informal_phrases = ['gonna', 'wanna', 'kinda', 'stuff', 'things']
    professional = not any(phrase in pred.answer.lower() for phrase in informal_phrases)
    
    # Different weighting for optimization vs evaluation
    if trace is None:  # Evaluation mode - balanced scoring
        return (correct * 0.5 +          # 50% correctness
                length_score * 0.2 +      # 20% appropriate length
                has_structure * 0.15 +    # 15% structure
                professional * 0.15)      # 15% tone
    else:  # Optimization mode - prioritize correctness
        # During optimization, focus on must-have criteria
        return correct and length_score > 0.5

def adaptive_summary_metric(example, pred, trace=None):
    """
    Advanced multi-criteria metric for summarization tasks.
    Adapts criteria based on source text characteristics.
    """
    source_length = len(example.source_text.split())
    summary_length = len(pred.summary.split())
    
    # Adaptive compression ratio based on source length
    if source_length < 100:
        ideal_ratio = 0.8  # Short texts need less compression
    elif source_length < 500:
        ideal_ratio = 0.3  # Medium texts: moderate compression
    else:
        ideal_ratio = 0.1  # Long texts: high compression
    
    actual_ratio = summary_length / source_length
    compression_score = 1.0 - abs(actual_ratio - ideal_ratio) / ideal_ratio
    
    # Key information retention (simple keyword overlap)
    if hasattr(example, 'key_points'):
        retained = sum(1 for point in example.key_points if point.lower() in pred.summary.lower())
        retention_score = retained / len(example.key_points)
    else:
        retention_score = 0.5  # Neutral if no key points specified
    
    # Coherence check (simple proxy: has intro and conclusion)
    sentences = pred.summary.split('.')
    coherence_score = 0.5
    if len(sentences) >= 3:  # Minimum structure
        coherence_score = 1.0
    
    # Weighted combination
    return (compression_score * 0.3 + 
            retention_score * 0.5 + 
            coherence_score * 0.2)
\`\`\`

#### Context Grounding

For retrieval-augmented generation (RAG) systems, ensuring that outputs are grounded in retrieved context is crucial for factual accuracy and reducing hallucinations. These metrics verify that generated content is supported by evidence:

\`\`\`python
def grounded_metric(example, pred, trace=None):
    """
    Check if answer is properly grounded in retrieved context.
    
    Essential for:
    - RAG systems
    - Question answering with sources
    - Any task where factual accuracy is critical
    """
    
    # Ensure prediction has context (from retrieval)
    if not hasattr(pred, 'context') or not pred.context:
        return 0.0  # No context = automatic fail
    
    # Combine all context passages
    context_text = ' '.join(pred.context).lower()
    
    # Method 1: Word-level grounding check
    answer_words = pred.answer.lower().split()
    content_words = [w for w in answer_words if len(w) > 3]  # Focus on meaningful words
    
    grounded_words = sum(1 for word in content_words if word in context_text)
    word_grounding = grounded_words / len(content_words) if content_words else 0.0
    
    # Method 2: N-gram grounding (catches phrases)
    def get_ngrams(text, n=3):
        words = text.split()
        return [' '.join(words[i:i+n]) for i in range(len(words)-n+1)]
    
    answer_ngrams = get_ngrams(pred.answer.lower(), 3)
    grounded_ngrams = sum(1 for ng in answer_ngrams if ng in context_text)
    ngram_grounding = grounded_ngrams / len(answer_ngrams) if answer_ngrams else 0.0
    
    # Method 3: Semantic grounding (key concepts)
    if hasattr(example, 'key_concepts'):
        mentioned_concepts = sum(1 for concept in example.key_concepts 
                               if concept.lower() in pred.answer.lower())
        grounded_concepts = sum(1 for concept in example.key_concepts 
                              if concept.lower() in context_text)
        concept_grounding = mentioned_concepts / grounded_concepts if grounded_concepts else 0.0
    else:
        concept_grounding = word_grounding  # Fallback
    
    # Also verify correctness
    correct = example.answer.lower() in pred.answer.lower()
    
    # Combine grounding signals
    grounding_score = (word_grounding * 0.3 + 
                      ngram_grounding * 0.4 + 
                      concept_grounding * 0.3)
    
    if trace is None:  # Evaluation
        return (grounding_score * 0.7 + correct * 0.3)
    else:  # Optimization - enforce minimum grounding
        return grounding_score > 0.6 and correct

def citation_accuracy(example, pred, trace=None):
    """
    Verify that citations are used correctly and point to real sources.
    """
    import re
    
    # Extract citations from answer
    citations = re.findall(r'\\[(\\d+)\\]', pred.answer)
    
    if not citations and len(pred.answer) > 100:
        return 0.0  # Long answers should have citations
    
    # Check if cited sources exist
    if hasattr(pred, 'sources'):
        valid_citations = all(int(c) <= len(pred.sources) for c in citations)
    else:
        valid_citations = False
    
    # Check if citations appear near relevant content
    citation_placement = 0.0
    for citation in citations:
        # Simple heuristic: citations should appear after statements, not randomly
        pattern = r'[.!?]\\s*\\[' + citation + r'\\]'
        if re.search(pattern, pred.answer):
            citation_placement += 1
    
    citation_placement = citation_placement / len(citations) if citations else 1.0
    
    return (valid_citations * 0.5 + citation_placement * 0.5)
\`\`\`

### AI-Powered Metrics

Traditional metrics rely on surface-level pattern matching or rule-based heuristics. AI-powered metrics leverage language models to understand semantic meaning, evaluate nuanced quality dimensions, and provide human-like judgment at scale. This is particularly powerful for tasks where quality is subjective or multifaceted.

#### Using LMs for Evaluation

The key insight is that language models can serve as sophisticated judges, evaluating outputs based on semantic understanding rather than syntactic matching. This enables metrics that capture what humans actually care about:

\`\`\`python
class SemanticSimilarity(dspy.Signature):
    """
    Assess if two texts convey the same meaning.
    
    This signature turns an LM into a semantic judge that understands
    paraphrasing, synonyms, and conceptual equivalence.
    """
    text1: str = dspy.InputField()
    text2: str = dspy.InputField()
    similar: bool = dspy.OutputField()
    explanation: str = dspy.OutputField(desc="Brief reasoning for the judgment")

def semantic_match(example, pred, trace=None):
    """
    Use LM to check semantic similarity between gold and predicted answers.
    
    Benefits over exact match:
    - Handles paraphrasing ("The capital is Paris" vs "Paris is the capital")
    - Understands synonyms ("automobile" vs "car")
    - Recognizes equivalent expressions ("50%" vs "half")
    """
    judge = dspy.Predict(SemanticSimilarity)
    
    # Get LM judgment with reasoning
    result = judge(
        text1=example.answer, 
        text2=pred.answer
    )
    
    # For debugging, you could log the explanation
    if trace and not result.similar:
        print(f"Semantic mismatch: {result.explanation}")
    
    return result.similar

# More sophisticated AI metric with graded scoring
class AnswerQuality(dspy.Signature):
    """
    Evaluate answer quality across multiple dimensions.
    
    This signature enables nuanced evaluation that mirrors
    how a human expert would assess answer quality.
    """
    question: str = dspy.InputField()
    answer: str = dspy.InputField()
    gold_answer: str = dspy.InputField(desc="Reference answer for comparison")
    
    correctness: float = dspy.OutputField(desc="0-1 score for factual accuracy")
    completeness: float = dspy.OutputField(desc="0-1 score for addressing all aspects")
    clarity: float = dspy.OutputField(desc="0-1 score for clear communication")
    overall: float = dspy.OutputField(desc="0-1 overall quality score")

def ai_quality_metric(example, pred, trace=None):
    """
    Comprehensive AI-based evaluation with multiple quality dimensions.
    
    This metric provides rich feedback for optimization and can identify
    specific areas for improvement beyond simple correct/incorrect.
    """
    # Use ChainOfThought for more reliable scoring
    evaluator = dspy.ChainOfThought(AnswerQuality)
    
    scores = evaluator(
        question=example.question,
        answer=pred.answer,
        gold_answer=example.answer
    )
    
    # You can use individual scores or combine them
    if trace is None:
        # During evaluation, return overall score
        return scores.overall
    else:
        # During optimization, could be stricter
        return scores.correctness > 0.8 and scores.completeness > 0.7

# Advanced pattern: Comparative evaluation
def comparative_quality_metric(example, pred, trace=None):
    """
    Compare predicted answer against multiple reference answers.
    Useful when there are multiple valid approaches.
    """
    if not hasattr(example, 'alternative_answers'):
        return ai_quality_metric(example, pred, trace)
    
    # Evaluate against each reference
    best_score = 0.0
    for alt_answer in [example.answer] + example.alternative_answers:
        alt_example = dspy.Example(
            question=example.question,
            answer=alt_answer
        ).with_inputs("question")
        
        score = ai_quality_metric(alt_example, pred, trace)
        best_score = max(best_score, score)
    
    return best_score
\`\`\`

#### Built-in AI Metrics

DSPy provides several pre-built AI-powered metrics that handle common evaluation patterns. These metrics are battle-tested and optimized for reliability:

\`\`\`python
from dspy.evaluate import SemanticF1

# Semantic F1 - measures overlap of key ideas rather than exact words
# Perfect for tasks where multiple phrasings are acceptable
semantic_f1 = SemanticF1()
score = semantic_f1(example, pred)

# Decompositional mode provides step-by-step reasoning
# This helps identify which specific ideas were missed
semantic_f1_detailed = SemanticF1(decompositional=True)

# The decompositional approach breaks down both texts into key ideas,
# matches them semantically, then calculates precision/recall
# Example output: "Identified 5 key ideas in gold, 4 in prediction, 3 matches"

# Custom semantic similarity with configurable threshold
from dspy.evaluate import AnswerSemanticSimilarity
semantic_sim = AnswerSemanticSimilarity(threshold=0.8)  # 80% similarity required

# For tasks requiring faithful reasoning
from dspy.evaluate import AnswerFaithfulness
faithfulness = AnswerFaithfulness()  # Checks if answer follows from context
\`\`\`

### Metrics for Different Task Types

Different NLP tasks require specialized metrics that capture their unique quality criteria. Here's how to design effective metrics for common task types:

#### Classification Metrics

Classification tasks range from simple binary decisions to complex multi-label scenarios. Your metrics should handle the specific characteristics of your classification problem:

\`\`\`python
def classification_metric(example, pred, trace=None):
    """
    Flexible metric for various classification scenarios.
    
    Handles:
    - Single-label classification (spam/not-spam)
    - Multi-label classification (topics, tags)
    - Hierarchical classification (categories with subcategories)
    """
    # Scenario 1: Single-label classification
    if hasattr(example, 'label'):
        predicted = pred.label.lower().strip()
        expected = example.label.lower().strip()
        
        # Handle common variations
        label_mappings = {
            'positive': ['pos', 'positive', '1', 'true'],
            'negative': ['neg', 'negative', '0', 'false'],
            'neutral': ['neu', 'neutral', 'none', 'n/a']
        }
        
        # Normalize labels
        for canonical, variants in label_mappings.items():
            if predicted in variants:
                predicted = canonical
            if expected in variants:
                expected = canonical
        
        return predicted == expected
    
    # Scenario 2: Multi-label classification
    if hasattr(example, 'labels'):
        gold_set = set(example.labels)
        pred_set = set(pred.labels) if hasattr(pred, 'labels') else set()
        
        if not gold_set:  # No labels expected
            return float(not pred_set)  # Correct if no labels predicted
        
        # Calculate metrics
        true_positives = len(gold_set & pred_set)
        false_positives = len(pred_set - gold_set)
        false_negatives = len(gold_set - pred_set)
        
        precision = true_positives / (true_positives + false_positives) if pred_set else 0
        recall = true_positives / (true_positives + false_negatives) if gold_set else 0
        
        if trace is None:  # Evaluation mode
            # F1 score balances precision and recall
            if precision + recall == 0:
                return 0.0
            f1 = 2 * precision * recall / (precision + recall)
            
            # Could also return weighted by label importance
            if hasattr(example, 'label_weights'):
                weighted_recall = sum(
                    example.label_weights.get(label, 1.0) 
                    for label in gold_set & pred_set
                ) / sum(
                    example.label_weights.get(label, 1.0) 
                    for label in gold_set
                )
                return (f1 + weighted_recall) / 2
            
            return f1
        else:  # Optimization mode
            # During optimization, prioritize recall (not missing important labels)
            return recall > 0.8
\`\`\`

#### Generation Metrics

Text generation tasks like summarization, translation, or creative writing need metrics that balance multiple quality dimensions:

\`\`\`python
from rouge_score import rouge_scorer
import nltk
from nltk.translate.bleu_score import sentence_bleu

def generation_metric(example, pred, trace=None):
    """
    Comprehensive metric for text generation tasks.
    
    Combines multiple signals:
    - Surface overlap (ROUGE)
    - Semantic preservation (key points)
    - Fluency and coherence
    - Length appropriateness
    """
    
    # 1. ROUGE score for surface-level similarity
    scorer = rouge_scorer.RougeScorer(['rouge1', 'rouge2', 'rougeL'], use_stemmer=True)
    scores = scorer.score(example.summary, pred.summary)
    
    # Use ROUGE-L as primary signal (captures longest common subsequence)
    rouge_l = scores['rougeL'].fmeasure
    
    # 2. Length appropriateness (important for summaries)
    target_length = len(example.summary.split())
    pred_length = len(pred.summary.split())
    length_ratio = pred_length / target_length if target_length > 0 else 0
    
    # Penalize both too short and too long
    if length_ratio < 0.5:
        length_score = length_ratio * 2  # Linear penalty for too short
    elif length_ratio > 2.0:
        length_score = 2.0 / length_ratio  # Inverse penalty for too long
    else:
        length_score = 1.0  # Perfect range
    
    # 3. Key information preservation (domain-specific)
    information_score = 1.0
    if hasattr(example, 'key_points'):
        # Check how many key points are covered
        mentioned = sum(
            1 for point in example.key_points 
            if point.lower() in pred.summary.lower()
        )
        information_score = mentioned / len(example.key_points) if example.key_points else 1.0
    
    # 4. Fluency check (simple heuristic)
    sentences = pred.summary.split('.')
    # Check for basic sentence structure
    fluency_score = 1.0
    if len(sentences) < 2:
        fluency_score = 0.5  # Too few sentences
    elif any(len(s.strip().split()) < 3 for s in sentences if s.strip()):
        fluency_score = 0.7  # Very short sentences
    
    # Combine signals based on task priorities
    if trace is None:  # Evaluation mode
        return (
            rouge_l * 0.4 +           # Surface similarity
            length_score * 0.2 +      # Appropriate length
            information_score * 0.3 + # Key points covered
            fluency_score * 0.1       # Basic fluency
        )
    else:  # Optimization mode
        # Stricter requirements during training
        return rouge_l > 0.4 and information_score > 0.6 and length_score > 0.5
\`\`\`

### Advanced Metric Patterns

Advanced metrics go beyond simple scoring to provide deeper insights into model behavior and enable more sophisticated optimization strategies:

#### Trace-Aware Metrics

The trace parameter in DSPy metrics provides access to the full execution history of your program. This enables metrics that evaluate not just the final output but the reasoning process itself:

\`\`\`python
def reasoning_quality_metric(example, pred, trace=None):
    """
    Evaluate the quality of reasoning steps, not just the final answer.
    
    This is crucial for tasks where the thought process matters:
    - Mathematical problem solving
    - Legal reasoning
    - Medical diagnosis
    - Complex analysis tasks
    """
    
    if trace is None:
        # Without trace, fall back to answer checking
        return example.answer.lower() in pred.answer.lower()
    
    # Extract reasoning steps from trace
    reasoning_steps = []
    for call in trace:
        if 'reasoning' in call[-1]:  # Last element is outputs
            reasoning_steps.append(call[-1].reasoning)
    
    # Check reasoning quality dimensions
    has_steps = len(reasoning_steps) > 0
    steps_logical = all(len(step) > 20 for step in reasoning_steps)  # Not too short
    no_repetition = len(set(reasoning_steps)) == len(reasoning_steps)
    
    # Check if reasoning leads to correct answer
    correct_answer = example.answer.lower() in pred.answer.lower()
    
    # During optimization, ensure good reasoning process
    if trace:
        return has_steps and steps_logical and no_repetition and correct_answer
    else:
        # During evaluation, weight different aspects
        return (correct_answer * 0.6 + 
                has_steps * 0.2 + 
                steps_logical * 0.1 + 
                no_repetition * 0.1)

def execution_efficiency_metric(example, pred, trace=None):
    """
    Evaluate computational efficiency of the solution.
    Important for production systems with latency requirements.
    """
    if trace is None:
        # Can't measure efficiency without trace
        return 1.0 if hasattr(pred, 'answer') else 0.0
    
    # Count module calls
    num_calls = len(trace)
    
    # Penalize excessive calls (e.g., too many retrieval attempts)
    if num_calls <= 3:
        efficiency_score = 1.0
    elif num_calls <= 5:
        efficiency_score = 0.8
    elif num_calls <= 10:
        efficiency_score = 0.6
    else:
        efficiency_score = 0.4
    
    # Also check answer correctness
    correct = example.answer.lower() in pred.answer.lower()
    
    # Balance efficiency with correctness
    return efficiency_score * 0.3 + correct * 0.7
\`\`\`

#### Composite Metrics

Real-world evaluation often requires balancing multiple objectives. Composite metrics provide a framework for combining different quality signals into a single optimization target:

\`\`\`python
class MetricSuite:
    """
    Combine multiple metrics with configurable weights and aggregation strategies.
    
    This pattern is essential when:
    - You have multiple quality criteria that matter
    - Different metrics may conflict (e.g., brevity vs completeness)
    - You need different behavior during optimization vs evaluation
    """
    
    def __init__(self, metrics_weights):
        """
        Args:
            metrics_weights: List of (metric_fn, weight) tuples
                           or dict of {name: (metric_fn, weight)}
        """
        if isinstance(metrics_weights, dict):
            self.named_metrics = metrics_weights
            self.metrics_weights = list(metrics_weights.values())
        else:
            self.named_metrics = None
            self.metrics_weights = metrics_weights
            
        # Normalize weights to sum to 1.0
        total_weight = sum(w for _, w in self.metrics_weights)
        self.metrics_weights = [(m, w/total_weight) for m, w in self.metrics_weights]
    
    def __call__(self, example, pred, trace=None):
        """
        Evaluate with different strategies for optimization vs evaluation.
        """
        if trace is None:
            # Evaluation mode: weighted average for nuanced scoring
            scores = {}
            weighted_sum = 0.0
            
            for i, (metric, weight) in enumerate(self.metrics_weights):
                score = metric(example, pred, trace)
                weighted_sum += score * weight
                
                # Track individual scores if named
                if self.named_metrics:
                    metric_name = list(self.named_metrics.keys())[i]
                    scores[metric_name] = score
            
            # Could return both aggregate and individual scores
            return weighted_sum
            
        else:
            # Optimization mode: different strategies possible
            scores = [metric(example, pred, trace) for metric, _ in self.metrics_weights]
            
            # Strategy 1: All must pass (strictest)
            # return all(scores)
            
            # Strategy 2: Weighted threshold (balanced)
            weighted_sum = sum(s * w for s, (_, w) in zip(scores, self.metrics_weights))
            return weighted_sum > 0.7  # Configurable threshold
            
            # Strategy 3: Critical metrics must pass
            # critical_indices = [0, 1]  # First two metrics are critical
            # return all(scores[i] for i in critical_indices)

# Usage Example: Multi-objective evaluation
combined_metric = MetricSuite({
    'correctness': (exact_match, 0.5),      # Most important
    'relevance': (semantic_match, 0.3),     # Also important
    'brevity': (length_appropriate, 0.1),   # Nice to have
    'grounding': (grounded_metric, 0.1)     # Nice to have
})

# Advanced: Hierarchical metrics with fallbacks
class HierarchicalMetric:
    """Try strict metric first, fall back to lenient if needed."""
    
    def __init__(self, strict_metric, lenient_metric, strict_threshold=0.9):
        self.strict_metric = strict_metric
        self.lenient_metric = lenient_metric
        self.strict_threshold = strict_threshold
    
    def __call__(self, example, pred, trace=None):
        strict_score = self.strict_metric(example, pred, trace)
        
        if strict_score >= self.strict_threshold:
            return strict_score
        else:
            # Use lenient metric but cap the score
            lenient_score = self.lenient_metric(example, pred, trace)
            return min(lenient_score * 0.8, self.strict_threshold)
\`\`\`

## 🧪 Running Evaluations

Evaluation is where theory meets reality. This section covers practical patterns for running evaluations efficiently, analyzing results systematically, and deriving actionable insights from your experiments.

### Basic Evaluation Loop

Sometimes you need full control over the evaluation process. This basic pattern gives you complete visibility into what's happening:

\`\`\`python
def evaluate_program(program, dataset, metric):
    """
    Simple but powerful evaluation loop with detailed tracking.
    
    Use this when you need:
    - Custom logging or monitoring
    - Detailed error analysis
    - Integration with external tools
    """
    scores = []
    predictions = []
    errors = []
    
    print(f"Evaluating {len(dataset)} examples...")
    start_time = time.time()
    
    for i, example in enumerate(dataset):
        try:
            # Get prediction with timing
            pred_start = time.time()
            pred = program(**example.inputs())
            pred_time = time.time() - pred_start
            
            predictions.append(pred)
            
            # Calculate score
            score = metric(example, pred)
            scores.append(score)
            
            # Track slow predictions
            if pred_time > 1.0:  # Longer than 1 second
                print(f"Slow prediction ({pred_time:.2f}s) for example {i}")
            
            # Optional: Print progress with running average
            if (i + 1) % 10 == 0:
                avg_so_far = sum(scores) / len(scores)
                elapsed = time.time() - start_time
                rate = (i + 1) / elapsed
                eta = (len(dataset) - i - 1) / rate
                
                print(f"Progress: {i+1}/{len(dataset)} | "
                      f"Avg: {avg_so_far:.3f} | "
                      f"Rate: {rate:.1f} ex/s | "
                      f"ETA: {eta:.0f}s")
                      
        except Exception as e:
            print(f"Error on example {i}: {e}")
            errors.append((i, example, str(e)))
            scores.append(0.0)  # or handle differently
            predictions.append(None)
    
    # Calculate final metrics with confidence intervals
    avg_score = sum(scores) / len(scores)
    std_score = (sum((s - avg_score)**2 for s in scores) / len(scores))**0.5
    
    print(f"\\n{'='*50}")
    print(f"Final Score: {avg_score:.3f} ± {1.96 * std_score / len(scores)**0.5:.3f}")
    print(f"Total time: {time.time() - start_time:.1f}s")
    print(f"Errors: {len(errors)}")
    
    return {
        'scores': scores,
        'predictions': predictions,
        'errors': errors,
        'avg_score': avg_score,
        'std_score': std_score
    }
\`\`\`

### Using DSPy's Evaluate

DSPy's built-in evaluator handles the complexity of parallel evaluation, progress tracking, and result visualization. It's the recommended approach for most use cases:

\`\`\`python
from dspy.evaluate import Evaluate

# Basic evaluation with sensible defaults
evaluator = Evaluate(
    devset=devset,
    metric=metric,
    num_threads=8,          # Parallel evaluation for speed
    display_progress=True,  # Show live progress bar
    display_table=5         # Show 5 example results
)

# Run evaluation - returns average score
score = evaluator(program)
print(f"Score: {score:.3%}")

# Advanced evaluation with custom configuration
evaluator = Evaluate(
    devset=devset,
    metric=metric,
    num_threads=16,              # More parallelism
    display_progress=True,
    display_table=10,            # Show more examples
    return_all_scores=True,      # Get individual scores
    return_outputs=True,         # Get all predictions
    provide_traceback=True       # Show errors in detail
)

# Get detailed results
results = evaluator(program, return_all_scores=True)
# results contains: score, all_scores, all_outputs, all_examples

# Analyze score distribution
import numpy as np
scores = results.all_scores
print(f"Mean: {np.mean(scores):.3f}")
print(f"Std: {np.std(scores):.3f}")
print(f"Min: {np.min(scores):.3f}")
print(f"Max: {np.max(scores):.3f}")
print(f"Median: {np.median(scores):.3f}")
\`\`\`

### Advanced Evaluation Options

Production systems need sophisticated evaluation strategies that go beyond simple average scores:

\`\`\`python
# Multi-metric evaluation for comprehensive assessment
def comprehensive_evaluation(program, dataset, output_dir="eval_results"):
    """
    Run multiple metrics and generate detailed reports.
    Essential for understanding system performance holistically.
    """
    import json
    from datetime import datetime
    
    metrics = {
        'exact_match': exact_match,
        'semantic_f1': SemanticF1(),
        'grounded': grounded_metric,
        'quality': ai_quality_metric,
        'efficiency': execution_efficiency_metric
    }
    
    results = {
        'timestamp': datetime.now().isoformat(),
        'dataset_size': len(dataset),
        'metrics': {}
    }
    
    # Run each metric
    for metric_name, metric_fn in metrics.items():
        print(f"\\nEvaluating {metric_name}...")
        
        evaluator = Evaluate(
            devset=dataset,
            metric=metric_fn,
            num_threads=16,
            display_progress=True,
            display_table=0,  # Don't display for each metric
            return_all_scores=True
        )
        
        metric_results = evaluator(program)
        score = metric_results if isinstance(metric_results, float) else metric_results.score
        
        results['metrics'][metric_name] = {
            'average': score,
            'scores': metric_results.all_scores if hasattr(metric_results, 'all_scores') else None
        }
        
        print(f"{metric_name}: {score:.3f}")
    
    # Save results
    os.makedirs(output_dir, exist_ok=True)
    with open(f"{output_dir}/eval_{datetime.now():%Y%m%d_%H%M%S}.json", 'w') as f:
        json.dump(results, f, indent=2)
    
    return results

# Advanced error analysis with categorization
def error_analysis(program, dataset, metric, error_threshold=0.5):
    """
    Detailed analysis of failure cases with pattern detection.
    Crucial for identifying systematic issues.
    """
    errors = []
    error_categories = defaultdict(list)
    
    print("Running error analysis...")
    
    for i, example in enumerate(dataset):
        try:
            pred = program(**example.inputs())
            score = metric(example, pred)
            
            if score < error_threshold:
                error_info = {
                    'index': i,
                    'score': score,
                    'input': example.inputs(),
                    'expected': example.labels(),
                    'predicted': {k: v for k, v in vars(pred).items() if not k.startswith('_')},
                }
                
                # Categorize error
                if hasattr(example, 'question_type'):
                    error_categories[example.question_type].append(error_info)
                
                # Analyze error type
                if score == 0:
                    error_info['error_type'] = 'complete_failure'
                elif score < 0.3:
                    error_info['error_type'] = 'major_error'
                else:
                    error_info['error_type'] = 'minor_error'
                
                errors.append(error_info)
                
        except Exception as e:
            errors.append({
                'index': i,
                'score': 0,
                'error': str(e),
                'error_type': 'exception'
            })
    
    # Analyze patterns
    total_examples = len(dataset)
    error_rate = len(errors) / total_examples
    
    print(f"\\n{'='*60}")
    print(f"Error Analysis Summary")
    print(f"{'='*60}")
    print(f"Total errors: {len(errors)}/{total_examples} ({error_rate:.1%})")
    
    # Error type breakdown
    error_types = defaultdict(int)
    for error in errors:
        error_types[error.get('error_type', 'unknown')] += 1
    
    print(f"\\nError Types:")
    for error_type, count in sorted(error_types.items(), key=lambda x: -x[1]):
        print(f"  {error_type}: {count} ({count/total_examples:.1%})")
    
    # Category breakdown if available
    if error_categories:
        print(f"\\nErrors by Category:")
        for category, cat_errors in error_categories.items():
            print(f"  {category}: {len(cat_errors)} errors")
    
    # Show examples of each error type
    print(f"\\nExample Errors:")
    for error_type in error_types:
        print(f"\\n{error_type.upper()}:")
        examples = [e for e in errors if e.get('error_type') == error_type][:2]
        for example in examples:
            print(f"  Example {example['index']}:")
            print(f"    Input: {example.get('input', 'N/A')}")
            print(f"    Expected: {example.get('expected', 'N/A')}")
            print(f"    Got: {example.get('predicted', 'N/A')}")
            if 'error' in example:
                print(f"    Error: {example['error']}")
    
    return errors
\`\`\`

### Comparative Evaluation

Comparing multiple approaches is essential for making informed architectural decisions. This section provides patterns for systematic comparison:

\`\`\`python
def compare_programs(programs, dataset, metric, detailed_analysis=True):
    """
    Compare multiple program variants systematically.
    
    Essential for:
    - Architecture decisions (CoT vs RAG vs Basic)
    - Optimization validation (before/after)
    - A/B testing different approaches
    """
    results = {}
    detailed_results = {}
    
    print(f"Comparing {len(programs)} programs on {len(dataset)} examples...")
    print("=" * 70)
    
    for name, program in programs.items():
        print(f"\\nEvaluating {name}...")
        
        evaluator = Evaluate(
            devset=dataset,
            metric=metric,
            num_threads=8,
            display_progress=True,
            display_table=2,  # Show sample outputs
            return_all_scores=detailed_analysis
        )
        
        if detailed_analysis:
            eval_result = evaluator(program)
            score = eval_result.score if hasattr(eval_result, 'score') else eval_result
            detailed_results[name] = eval_result
        else:
            score = evaluator(program)
            
        results[name] = score
    
    # Statistical comparison
    print("\\n" + "=" * 70)
    print("COMPARISON RESULTS")
    print("=" * 70)
    
    # Ranking
    ranked = sorted(results.items(), key=lambda x: x[1], reverse=True)
    for rank, (name, score) in enumerate(ranked, 1):
        delta = score - ranked[0][1] if rank > 1 else 0
        print(f"{rank}. {name:<20} {score:.3f} {'📈' if rank == 1 else f'({delta:+.3f})'}")
    
    # Statistical significance test (simplified)
    if detailed_analysis and len(programs) == 2:
        names = list(detailed_results.keys())
        scores1 = detailed_results[names[0]].all_scores
        scores2 = detailed_results[names[1]].all_scores
        
        # Paired t-test approximation
        from scipy import stats
        t_stat, p_value = stats.ttest_rel(scores1, scores2)
        
        print(f"\\nStatistical Test ({names[0]} vs {names[1]}):")
        print(f"  p-value: {p_value:.4f} {'✅ Significant' if p_value < 0.05 else '❌ Not significant'}")
    
    return results, detailed_results

# Real-world usage comparing architectures
programs = {
    'baseline': dspy.Predict("question -> answer"),
    'cot': dspy.ChainOfThought("question -> answer"),
    'cot_with_examples': dspy.ChainOfThoughtWithHint("question -> answer"),
    'rag': RAGSystem(k=5),
    'optimized': optimized_program
}

results, details = compare_programs(programs, devset, semantic_f1)

# Analyze specific failure modes
def analyze_disagreements(programs, dataset, metric):
    """Find examples where programs disagree significantly."""
    disagreements = []
    
    for i, example in enumerate(dataset):
        predictions = {}
        scores = {}
        
        for name, program in programs.items():
            pred = program(**example.inputs())
            score = metric(example, pred)
            predictions[name] = pred
            scores[name] = score
        
        # Check for significant disagreement
        score_variance = np.var(list(scores.values()))
        if score_variance > 0.1:  # Significant disagreement
            disagreements.append({
                'example_id': i,
                'input': example.inputs(),
                'scores': scores,
                'predictions': predictions
            })
    
    print(f"\\nFound {len(disagreements)} examples with significant disagreement")
    return disagreements
\`\`\`

### Evaluation-Driven Development

The most successful DSPy projects follow an evaluation-driven development workflow. This approach ensures continuous improvement and prevents regression:

\`\`\`python
class EvaluationPipeline:
    """
    Systematic evaluation workflow for production development.
    
    Features:
    - Track all experiments with versioning
    - Prevent test set contamination
    - Support ablation studies
    - Generate improvement insights
    """
    
    def __init__(self, metric, train_set, dev_set, test_set):
        self.metric = metric
        self.train_set = train_set
        self.dev_set = dev_set
        self.test_set = test_set
        self.results_history = []
        self.best_dev_score = 0
        self.best_program = None
        
        # Ensure test set is only used once
        self._test_set_used = False
    
    def evaluate_and_record(self, program, name, dataset_name='dev', 
                          save_predictions=False):
        """
        Evaluate program and maintain full history.
        
        Args:
            program: DSPy program to evaluate
            name: Descriptive name for this experiment
            dataset_name: 'train', 'dev', or 'test' (test only once!)
            save_predictions: Whether to save all predictions
        """
        # Prevent multiple test evaluations
        if dataset_name == 'test':
            if self._test_set_used:
                raise ValueError("Test set already used! Use dev set for iterations.")
            self._test_set_used = True
            print("⚠️  Using test set - this should be your final evaluation!")
        
        dataset = {
            'train': self.train_set,
            'dev': self.dev_set,
            'test': self.test_set
        }[dataset_name]
        
        evaluator = Evaluate(
            devset=dataset,
            metric=self.metric,
            num_threads=16,
            display_progress=True,
            return_all_scores=True,
            return_outputs=save_predictions
        )
        
        result = evaluator(program)
        score = result.score if hasattr(result, 'score') else result
        
        # Record results with full context
        record = {
            'experiment_name': name,
            'dataset': dataset_name,
            'score': score,
            'timestamp': datetime.now().isoformat(),
            'dataset_size': len(dataset),
            'program_type': type(program).__name__,
        }
        
        if hasattr(result, 'all_scores'):
            record['score_distribution'] = {
                'mean': np.mean(result.all_scores),
                'std': np.std(result.all_scores),
                'min': np.min(result.all_scores),
                'max': np.max(result.all_scores),
                'median': np.median(result.all_scores)
            }
        
        self.results_history.append(record)
        
        # Track best dev performance
        if dataset_name == 'dev' and score > self.best_dev_score:
            self.best_dev_score = score
            self.best_program = program
            print(f"🎉 New best dev score: {score:.3f}")
        
        return score, record
    
    def run_ablation_study(self, base_program, modifications):
        """
        Test impact of different modifications systematically.
        
        Args:
            base_program: Your current best program
            modifications: Dict of {name: modified_program}
        """
        print("\\n" + "="*60)
        print("ABLATION STUDY")
        print("="*60)
        
        # Baseline
        base_score, _ = self.evaluate_and_record(base_program, "baseline")
        ablation_results = {'baseline': {'score': base_score, 'delta': 0.0}}
        
        # Test each modification
        for mod_name, modified_program in modifications.items():
            score, _ = self.evaluate_and_record(modified_program, f"ablation_{mod_name}")
            delta = score - base_score
            print(f"{mod_name}: {score:.3f} ({delta:+.3f} from baseline)")
            ablation_results[mod_name] = {'score': score, 'delta': delta}
        
        # Summary
        print("\\nAblation Summary:")
        print("-" * 40)
        sorted_results = sorted(ablation_results.items(), 
                               key=lambda x: x[1]['score'], 
                               reverse=True)
        
        for name, result in sorted_results:
            impact = "🟢" if result['delta'] > 0 else ("🔴" if result['delta'] < 0 else "⚪")
            print(f"{impact} {name}: {result['score']:.3f} ({result['delta']:+.3f})")
        
        return ablation_results
    
    def progressive_evaluation(self, program, optimizer):
        """
        Evaluate through optimization stages to understand data efficiency.
        
        This reveals:
        - How quickly the system improves with more data
        - Diminishing returns points
        - Optimal training set size
        """
        print("\\n" + "="*60)
        print("PROGRESSIVE OPTIMIZATION EVALUATION")
        print("="*60)
        
        # Initial performance
        initial_score, _ = self.evaluate_and_record(program, "initial", "dev")
        print(f"Initial (unoptimized): {initial_score:.3f}")
        
        # Track improvement curve
        improvement_curve = [(0, initial_score)]
        
        # Optimize with different data sizes
        for n in [20, 50, 100, 200, len(self.train_set)]:
            if n > len(self.train_set):
                break
                
            print(f"\\nOptimizing with {n} examples...")
            subset = self.train_set[:n]
            
            optimized = optimizer.compile(
                program,
                trainset=subset,
                metric=self.metric
            )
            
            score, _ = self.evaluate_and_record(
                optimized, 
                f"optimized_n{n}",
                "dev"
            )
            
            improvement = score - initial_score
            improvement_curve.append((n, score))
            
            print(f"Score: {score:.3f} (improvement: {improvement:+.3f})")
        
        # Analyze improvement rate
        print("\\nData Efficiency Analysis:")
        for i in range(1, len(improvement_curve)):
            n_prev, score_prev = improvement_curve[i-1]
            n_curr, score_curr = improvement_curve[i]
            
            data_increase = n_curr - n_prev
            score_increase = score_curr - score_prev
            efficiency = score_increase / data_increase if data_increase > 0 else 0
            
            print(f"  {n_prev} → {n_curr} examples: "
                  f"{score_increase:+.4f} improvement "
                  f"(efficiency: {efficiency:.6f} per example)")
        
        # Final test evaluation (only if we have a good model)
        if self.best_dev_score > initial_score * 1.1:  # 10% improvement threshold
            print("\\n🎯 Running final test evaluation...")
            final_score, _ = self.evaluate_and_record(
                self.best_program,
                "final_test",
                "test"
            )
            print(f"Test score: {final_score:.3f}")
            print(f"Dev-Test gap: {self.best_dev_score - final_score:.3f}")
        else:
            print("\\n⚠️  Skipping test evaluation (insufficient improvement)")
        
        return improvement_curve
    
    def generate_report(self, output_file="evaluation_report.md"):
        """Generate comprehensive evaluation report."""
        report = []
        report.append("# DSPy Evaluation Report\\n")
        report.append(f"Generated: {datetime.now().strftime('%Y-%m-%d %H:%M:%S')}\\n")
        
        # Summary statistics
        report.append("## Summary\\n")
        report.append(f"- Total experiments: {len(self.results_history)}")
        report.append(f"- Best dev score: {self.best_dev_score:.3f}")
        report.append(f"- Test used: {'Yes' if self._test_set_used else 'No'}\\n")
        
        # Experiment history
        report.append("## Experiment History\\n")
        report.append("| Experiment | Dataset | Score | Timestamp |")
        report.append("|------------|---------|-------|-----------|")
        
        for result in self.results_history[-10:]:  # Last 10 experiments
            report.append(f"| {result['experiment_name']} | "
                         f"{result['dataset']} | "
                         f"{result['score']:.3f} | "
                         f"{result['timestamp']} |")
        
        # Save report
        with open(output_file, 'w') as f:
            f.write('\\n'.join(report))
        
        print(f"\\n📊 Report saved to {output_file}")

# Usage example
pipeline = EvaluationPipeline(
    metric=your_metric,
    train_set=train_examples,
    dev_set=dev_examples,
    test_set=test_examples
)

# Run experiments
baseline = dspy.Predict("question -> answer")
pipeline.evaluate_and_record(baseline, "baseline")

# Try improvements
cot = dspy.ChainOfThought("question -> answer")
pipeline.evaluate_and_record(cot, "chain_of_thought")

# Run ablation study
ablation_results = pipeline.run_ablation_study(
    base_program=cot,
    modifications={
        "no_reasoning": dspy.Predict("question -> answer"),
        "with_examples": dspy.ChainOfThoughtWithHint("question -> answer"),
        "temperature_0.7": cot.copy(temperature=0.7)
    }
)
\`\`\`

### Debugging with Evaluation

When things go wrong (and they will), systematic debugging is essential. This section provides practical patterns for understanding and fixing evaluation issues:

\`\`\`python
def debug_predictions(program, dataset, metric, num_examples=5, verbose=True):
    """
    Detailed debugging of predictions with full introspection.
    
    Use this when:
    - Metric scores are unexpectedly low
    - You need to understand failure patterns
    - Debugging specific edge cases
    """
    failures = []
    successes = []
    
    print("🔍 DETAILED PREDICTION DEBUGGING")
    print("=" * 70)
    
    for i, example in enumerate(dataset[:num_examples]):
        print(f"\\n{'='*70}")
        print(f"EXAMPLE {i+1}")
        print(f"{'='*70}")
        
        # Show input
        print("\\n📥 INPUT:")
        for key, value in example.inputs().items():
            # Truncate long inputs for readability
            display_value = str(value)[:200] + "..." if len(str(value)) > 200 else value
            print(f"  {key}: {display_value}")
        
        # Get prediction with trace capture
        dspy.settings.trace = []  # Clear trace
        
        try:
            pred = program(**example.inputs())
            prediction_success = True
        except Exception as e:
            print(f"\\n❌ PREDICTION ERROR: {e}")
            prediction_success = False
            pred = None
        
        if prediction_success:
            # Show prediction
            print("\\n📤 PREDICTION:")
            for key, value in vars(pred).items():
                if not key.startswith('_'):
                    display_value = str(value)[:200] + "..." if len(str(value)) > 200 else value
                    print(f"  {key}: {display_value}")
            
            # Show expected
            print("\\n✅ EXPECTED:")
            for key, value in example.labels().items():
                display_value = str(value)[:200] + "..." if len(str(value)) > 200 else value
                print(f"  {key}: {display_value}")
            
            # Calculate and analyze metric
            score = metric(example, pred)
            print(f"\\n📊 SCORE: {score}")
            
            # Categorize result
            if score >= 0.8:
                successes.append((i, example, pred, score))
                print("✅ Success")
            else:
                failures.append((i, example, pred, score))
                print("❌ Failure")
            
            # Show reasoning if available
            if hasattr(pred, 'reasoning') and pred.reasoning:
                print(f"\\n💭 REASONING:")
                print(textwrap.fill(pred.reasoning, width=70, initial_indent="  ", 
                                  subsequent_indent="  "))
            
            # Analyze trace if verbose
            if verbose and dspy.settings.trace:
                print("\\n🔄 LM CALLS:")
                for j, call in enumerate(dspy.settings.trace):
                    print(f"\\n  Call {j+1}:")
                    # Extract key information from trace
                    if isinstance(call, dict):
                        if 'prompt' in call:
                            print(f"    Prompt: {call['prompt'][:150]}...")
                        if 'response' in call:
                            print(f"    Response: {call['response'][:150]}...")
    
    # Summary analysis
    print("\\n" + "="*70)
    print("DEBUGGING SUMMARY")
    print("="*70)
    print(f"Total examples analyzed: {num_examples}")
    print(f"Successes: {len(successes)} ({len(successes)/num_examples:.1%})")
    print(f"Failures: {len(failures)} ({len(failures)/num_examples:.1%})")
    
    # Common failure patterns
    if failures:
        print("\\n🔍 FAILURE ANALYSIS:")
        failure_types = defaultdict(int)
        
        for idx, example, pred, score in failures:
            # Analyze failure type
            if pred is None:
                failure_types['exception'] += 1
            elif score == 0:
                failure_types['complete_mismatch'] += 1
            elif score < 0.5:
                failure_types['poor_match'] += 1
            else:
                failure_types['partial_match'] += 1
        
        for failure_type, count in failure_types.items():
            print(f"  {failure_type}: {count}")
    
    return {'successes': successes, 'failures': failures}

# Advanced debugging helper
class EvaluationDebugger:
    """Comprehensive debugging toolkit for evaluation issues."""
    
    def __init__(self, program, dataset, metric):
        self.program = program
        self.dataset = dataset
        self.metric = metric
    
    def find_edge_cases(self, threshold=0.3):
        """Identify examples with unusually low scores."""
        edge_cases = []
        
        for i, example in enumerate(self.dataset):
            pred = self.program(**example.inputs())
            score = self.metric(example, pred)
            
            if score < threshold:
                edge_cases.append({
                    'index': i,
                    'score': score,
                    'example': example,
                    'prediction': pred
                })
        
        return sorted(edge_cases, key=lambda x: x['score'])
    
    def analyze_metric_components(self, example_idx):
        """Break down composite metrics to understand failures."""
        example = self.dataset[example_idx]
        pred = self.program(**example.inputs())
        
        # If metric is composite, analyze components
        if hasattr(self.metric, 'metrics_weights'):
            print(f"\\nComponent Analysis for Example {example_idx}:")
            for metric_fn, weight in self.metric.metrics_weights:
                score = metric_fn(example, pred)
                print(f"  {metric_fn.__name__}: {score:.3f} (weight: {weight:.2f})")
        else:
            score = self.metric(example, pred)
            print(f"Single metric score: {score:.3f}")
\`\`\`

## 📋 Module 3 Summary

### What You've Learned

✅ **Building Development Sets**
- Working with DSPy Examples and input specifications
- Data collection strategies for different scales
- Converting from various data sources
- Dataset splitting best practices

✅ **Defining Metrics**
- Simple metrics for exact and partial matching
- Complex metrics for long-form outputs
- AI-powered evaluation with DSPy modules
- Task-specific metric design

✅ **Running Evaluations**
- Basic evaluation loops
- Using DSPy's Evaluate class
- Comparative analysis of programs
- Evaluation-driven development workflows

### Quick Reference

\`\`\`python
# Example creation
example = dspy.Example(input="...", output="...").with_inputs("input")

# Dataset splitting
trainset, devset, testset = data[:200], data[200:500], data[500:]

# Simple metric
def metric(example, pred, trace=None):
    return example.answer == pred.answer

# AI metric
judge = dspy.Predict("text1, text2 -> similar: bool")
score = judge(text1=example.answer, text2=pred.answer).similar

# Evaluation
evaluator = Evaluate(devset=devset, metric=metric, num_threads=8)
score = evaluator(program)
\`\`\`

### Best Practices

1. **Start Small**: 20-50 examples can get you started
2. **Iterate on Metrics**: Your metric defines success - refine it continuously
3. **Use Multiple Metrics**: No single metric captures everything
4. **Separate Dev/Test**: Never optimize on your test set
5. **Error Analysis**: Learn from failures to improve your system

### What's Next?

Module 4 will cover DSPy Optimization:
- Understanding how optimizers work
- Few-shot learning techniques
- Instruction optimization
- Advanced optimization strategies

You now have the tools to systematically evaluate and improve your DSPy programs!`,
  exercises: [
    {
      id: 'dspy-m3-ex1',
      title: 'Build a Comprehensive Answer Quality Evaluator',
      description:
        'Create a sophisticated metric system that evaluates answers across multiple quality dimensions',
      difficulty: 'medium',
      type: 'implementation',
      instructions: `## Context
You're working for an educational technology company that provides AI tutoring services. The platform serves thousands of students daily with math, science, and reading comprehension questions. The product team needs a robust evaluation system that can assess not just correctness, but also the overall quality and helpfulness of AI-generated answers.

Currently, the company uses simple exact-match evaluation, but this misses many nuances. A math answer might be mathematically correct but poorly explained, or a reading comprehension answer might contain the right information but be too verbose or unclear.

## Your Goal
Build a comprehensive evaluation metric that assesses multiple dimensions of answer quality:
1. **Correctness**: Is the core answer factually accurate?
2. **Completeness**: Does the answer address all parts of the question?
3. **Clarity**: Is the explanation clear and well-structured?
4. **Appropriateness**: Is the response length and detail level suitable?
5. **Educational Value**: Does the answer help students learn?

## Requirements
- Create a metric function that accepts example, prediction, and optional trace parameters
- Handle both exact numerical answers and open-ended explanations
- Implement tolerance-based checking for mathematical answers
- Use weighted scoring to combine multiple quality dimensions
- Provide detailed feedback that can be used for system improvement
- Test with various types of educational content

## Test Cases to Evaluate
**Mathematics:**
- Question: "What is 15% of 200?"
- Gold Answer: "30"
- Test Predictions: "30", "30.0", "Thirty", "15/100 × 200 = 30"

**Science Explanation:**
- Question: "Why do seasons occur?"
- Gold Answer: "Seasons occur because Earth's axis is tilted at 23.5 degrees relative to its orbit around the Sun."
- Test Predictions: [Various explanations of different quality levels]

**Reading Comprehension:**
- Question: "What was the main character's motivation?"
- Gold Answer: "The character was motivated by a desire to protect her family."
- Test Predictions: [Various interpretations and explanations]

## Starting Point
Think about how a human teacher would evaluate student responses. They don't just check if the answer is "right" - they consider effort, understanding, clarity of explanation, and learning progress. Your metric should capture this multidimensional assessment approach.`,
      hints: [
        'Start with a basic structure: def comprehensive_metric(example, pred, trace=None). The function signature follows DSPy conventions.',
        'For numerical answers, implement tolerance-based comparison: abs(gold_num - pred_num) <= tolerance',
        'Consider creating sub-metrics for different quality dimensions, then combining them with weights.',
        'Handle different data types gracefully - some answers are numbers, others are text explanations.',
        'Use the trace parameter differently for evaluation vs optimization: return float for evaluation, bool for optimization.',
        'For text comparison, consider both exact matching and semantic similarity approaches.',
        'Test edge cases: empty answers, very long answers, non-numeric text when numbers expected.',
        'Add logging or debug information to help understand why certain answers scored low or high.',
      ],
      solution: `import dspy
import re
from typing import Union

def comprehensive_quality_metric(example, pred, trace=None):
    """
    Comprehensive metric that evaluates answers across multiple quality dimensions.
    
    This metric embodies the philosophy that good educational AI should be:
    - Factually correct
    - Appropriately detailed  
    - Clear and well-structured
    - Helpful for learning
    """
    
    # Initialize score components
    correctness_score = 0.0
    completeness_score = 0.0
    clarity_score = 0.0
    appropriateness_score = 0.0
    
    # Get the answers for comparison
    gold_answer = str(example.answer).strip()
    pred_answer = str(pred.answer).strip()
    
    # 1. CORRECTNESS ASSESSMENT (40% weight)
    correctness_score = evaluate_correctness(gold_answer, pred_answer)
    
    # 2. COMPLETENESS ASSESSMENT (25% weight)  
    completeness_score = evaluate_completeness(gold_answer, pred_answer)
    
    # 3. CLARITY ASSESSMENT (20% weight)
    clarity_score = evaluate_clarity(pred_answer)
    
    # 4. APPROPRIATENESS ASSESSMENT (15% weight)
    appropriateness_score = evaluate_appropriateness(gold_answer, pred_answer)
    
    # Combine scores with weights
    if trace is None:
        # Evaluation mode: return weighted average (0-1 scale)
        final_score = (
            correctness_score * 0.40 +
            completeness_score * 0.25 +
            clarity_score * 0.20 +
            appropriateness_score * 0.15
        )
        return final_score
    else:
        # Optimization mode: return boolean (stricter requirements)
        return (correctness_score >= 0.8 and 
                completeness_score >= 0.7 and
                clarity_score >= 0.6)

def evaluate_correctness(gold_answer, pred_answer, tolerance=0.01):
    """Evaluate factual correctness with numerical tolerance."""
    
    # Try numerical comparison first
    try:
        # Extract numbers from strings (handles "30", "30.0", "thirty", etc.)
        gold_num = extract_number(gold_answer)
        pred_num = extract_number(pred_answer)
        
        if gold_num is not None and pred_num is not None:
            return 1.0 if abs(gold_num - pred_num) <= tolerance else 0.0
    except:
        pass
    
    # Fall back to text comparison
    if gold_answer.lower() == pred_answer.lower():
        return 1.0
    elif gold_answer.lower() in pred_answer.lower():
        return 0.8  # Partial credit for containing correct answer
    elif pred_answer.lower() in gold_answer.lower():
        return 0.6  # Some credit for partial match
    else:
        return 0.0

def evaluate_completeness(gold_answer, pred_answer):
    """Assess if the prediction addresses all aspects of the gold answer."""
    
    if not pred_answer:
        return 0.0
    
    # Simple heuristic: longer gold answers expect more comprehensive responses
    gold_length = len(gold_answer.split())
    pred_length = len(pred_answer.split())
    
    if gold_length <= 5:  # Short factual answers
        return 1.0 if pred_length >= 1 else 0.0
    elif gold_length <= 20:  # Medium explanations
        ratio = min(pred_length / gold_length, 1.0)
        return max(ratio, 0.3)  # At least partial credit
    else:  # Long explanations
        ratio = min(pred_length / (gold_length * 0.8), 1.0)
        return max(ratio, 0.2)

def evaluate_clarity(pred_answer):
    """Assess clarity and structure of the response."""
    
    if not pred_answer:
        return 0.0
    
    clarity_score = 0.5  # Base score
    
    # Bonus for proper sentence structure
    if pred_answer.endswith('.') or pred_answer.endswith('!') or pred_answer.endswith('?'):
        clarity_score += 0.2
    
    # Bonus for reasonable length (not too short, not too verbose)
    word_count = len(pred_answer.split())
    if 3 <= word_count <= 100:
        clarity_score += 0.2
    elif word_count > 100:
        clarity_score -= 0.1  # Penalty for verbosity
    
    # Bonus for avoiding repetitive text
    words = pred_answer.lower().split()
    unique_ratio = len(set(words)) / len(words) if words else 0
    if unique_ratio > 0.7:
        clarity_score += 0.1
    
    return min(clarity_score, 1.0)

def evaluate_appropriateness(gold_answer, pred_answer):
    """Assess if response length and detail are appropriate."""
    
    if not pred_answer:
        return 0.0
    
    gold_words = len(gold_answer.split())
    pred_words = len(pred_answer.split())
    
    # Expect similar verbosity levels
    if gold_words <= 5:  # Concise answers expected
        return 1.0 if pred_words <= 10 else 0.6
    elif gold_words <= 20:  # Moderate detail expected  
        return 1.0 if 5 <= pred_words <= 30 else 0.7
    else:  # Detailed explanation expected
        return 1.0 if pred_words >= 15 else 0.8

def extract_number(text):
    """Extract numerical value from text, handling various formats."""
    
    # Handle word numbers
    word_to_num = {
        'zero': 0, 'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
        'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
        'twenty': 20, 'thirty': 30, 'forty': 40, 'fifty': 50
    }
    
    text_lower = text.lower().strip()
    if text_lower in word_to_num:
        return float(word_to_num[text_lower])
    
    # Extract numeric values using regex
    numbers = re.findall(r'-?\\d+\\.?\\d*', text)
    if numbers:
        try:
            return float(numbers[0])
        except ValueError:
            pass
    
    return None

# Test the comprehensive metric
if __name__ == "__main__":
    import dspy
    
    # Test cases
    test_cases = [
        {
            "example": dspy.Example(question="What is 15% of 200?", answer="30"),
            "predictions": [
                dspy.Example(answer="30"),
                dspy.Example(answer="30.0"), 
                dspy.Example(answer="Thirty"),
                dspy.Example(answer="15/100 × 200 = 30")
            ]
        }
    ]
    
    for case in test_cases:
        print(f"Question: {case['example'].question}")
        print(f"Expected: {case['example'].answer}")
        print("-" * 40)
        
        for i, pred in enumerate(case['predictions']):
            score = comprehensive_quality_metric(case['example'], pred)
            print(f"Prediction {i+1}: {pred.answer}")
            print(f"Quality Score: {score:.3f}")
            print()`,
      testCases: [
        {
          input: 'comprehensive_quality_metric function with multiple dimensions',
          expected: true,
          description: 'Multi-dimensional quality assessment',
        },
        {
          input: 'extract_number',
          expected: true,
          description: 'Handles various number formats',
        },
      ],
    },
    {
      id: 'dspy-m3-ex2',
      title: 'Build a Robust Data Loading System',
      description:
        'Create a production-ready data loading pipeline that handles multiple file formats and data quality issues',
      difficulty: 'medium',
      type: 'implementation',
      instructions: `## Context
You're working for a data-driven AI research company that needs to process evaluation datasets from various sources. The team receives data in different formats (CSV, JSON, TSV) from research collaborators, clients, and public datasets. Currently, each project requires custom loading scripts, leading to inconsistent data handling and frequent bugs.

The data science team has identified several recurring issues:
- Missing values in critical fields causing evaluation failures
- Inconsistent field naming across datasets (question/query, answer/response, context/passage)
- Different encodings and delimiters breaking the loading process
- No validation of data quality before expensive model evaluations
- Difficulty tracking data provenance and transformations

## Your Goal
Build a comprehensive data loading system that can:
1. **Handle Multiple Formats**: Support CSV, JSON, and TSV files seamlessly
2. **Robust Error Handling**: Gracefully handle missing files, corrupted data, and encoding issues
3. **Flexible Field Mapping**: Map different field names to standard DSPy schema
4. **Data Quality Validation**: Ensure data meets quality standards before processing
5. **Comprehensive Logging**: Track all transformations and provide detailed diagnostics

## Requirements
- Create a \`DatasetLoader\` class that can handle CSV, JSON, and TSV files
- Implement field mapping to handle different naming conventions
- Add comprehensive error handling and validation
- Include data quality checks (missing values, empty strings, encoding issues)
- Provide detailed logging and statistics about the loaded data
- Support custom input field specifications for different dataset types
- Handle edge cases like different encodings, malformed files, and missing columns

## Test Cases to Handle
**Multi-format Support:**
- Load identical data from CSV, JSON, and TSV formats
- Verify data consistency across formats

**Field Mapping:**
- Map "query" to "question", "response" to "answer", "passage" to "context"
- Handle datasets with only question/answer vs full QA with context

**Error Recovery:**
- Handle files with missing columns gracefully
- Deal with corrupted or partially readable files
- Process files with mixed encoding issues

**Data Quality:**
- Filter out examples with empty questions or answers
- Handle examples with missing context fields appropriately
- Validate minimum data quality thresholds

## Starting Point
Think about how a production data pipeline should work. You need to balance flexibility (handling many formats) with reliability (ensuring data quality). Consider what information a data scientist would need to trust and debug the loading process.

Focus on creating a system that's both robust (handles errors gracefully) and informative (provides clear feedback about what happened during loading).`,
      hints: [
        'Start with a class-based design: class DatasetLoader. This allows you to maintain state and configuration.',
        'Use a dictionary for field mapping: {"query": "question", "response": "answer"} to handle different naming conventions.',
        'Implement separate methods for each file format (load_csv, load_json, load_tsv) then a unified load method.',
        'Add validation methods: validate_required_fields, check_data_quality, ensure_proper_encoding.',
        'Use try-except blocks extensively - file I/O is error-prone in production environments.',
        'Keep detailed statistics: track how many examples were loaded, filtered, or failed validation.',
        'For JSON files, handle both array of objects and object with array structure.',
        'Consider using pandas for CSV/TSV but also provide fallback for pure Python parsing.',
        'Add logging throughout the process so users can debug issues effectively.',
        'Test with edge cases: empty files, files with only headers, files with special characters.',
      ],
      solution: `import pandas as pd
import json
import csv
import logging
import os
from typing import List, Dict, Optional, Any, Tuple
import dspy

class DatasetLoader:
    """
    Production-ready data loading system for DSPy datasets.
    
    Handles multiple file formats, provides robust error handling,
    and ensures data quality for reliable model evaluation.
    """
    
    def __init__(self, field_mapping: Optional[Dict[str, str]] = None, 
                 required_fields: Optional[List[str]] = None,
                 encoding: str = 'utf-8'):
        """
        Initialize the data loader with flexible configuration.
        
        Args:
            field_mapping: Dict mapping source field names to target DSPy fields
            required_fields: List of fields that must be present  
            encoding: File encoding to use (default: utf-8)
        """
        # Default field mappings handle common naming variations
        self.field_mapping = field_mapping or {
            'query': 'question',
            'q': 'question', 
            'response': 'answer',
            'ans': 'answer',
            'a': 'answer',
            'passage': 'context',
            'document': 'context',
            'ctx': 'context'
        }
        
        # Standard required fields for most QA datasets
        self.required_fields = required_fields or ['question', 'answer']
        self.encoding = encoding
        
        # Statistics tracking for comprehensive reporting
        self.stats = {
            'total_rows': 0,
            'valid_examples': 0,
            'filtered_examples': 0,
            'errors': [],
            'field_mappings_applied': 0
        }
        
        # Configure logging for detailed diagnostics
        logging.basicConfig(level=logging.INFO)
        self.logger = logging.getLogger(__name__)
    
    def load_dataset(self, file_path: str, 
                    input_fields: Optional[List[str]] = None) -> List[dspy.Example]:
        """
        Main entry point - automatically detects format and loads data.
        
        Args:
            file_path: Path to the dataset file
            input_fields: Fields to mark as inputs (others become labels)
            
        Returns:
            List of validated DSPy Examples
        """
        if not os.path.exists(file_path):
            raise FileNotFoundError(f"Dataset file not found: {file_path}")
        
        # Reset statistics for this loading operation
        self._reset_stats()
        
        # Detect file format and route to appropriate loader
        file_ext = os.path.splitext(file_path)[1].lower()
        
        try:
            if file_ext == '.csv':
                raw_data = self._load_csv(file_path)
            elif file_ext == '.json':
                raw_data = self._load_json(file_path)
            elif file_ext in ['.tsv', '.txt']:
                raw_data = self._load_tsv(file_path)
            else:
                raise ValueError(f"Unsupported file format: {file_ext}")
            
            # Process and validate the loaded data
            processed_data = self._process_data(raw_data)
            examples = self._create_dspy_examples(processed_data, input_fields)
            
            # Generate comprehensive loading report
            self._log_loading_summary(file_path)
            
            return examples
            
        except Exception as e:
            self.logger.error(f"Failed to load dataset from {file_path}: {str(e)}")
            self.stats['errors'].append(f"Loading failed: {str(e)}")
            raise
    
    def _load_csv(self, file_path: str) -> List[Dict[str, Any]]:
        """Load data from CSV file with robust error handling."""
        try:
            # Try pandas first for robust CSV parsing
            df = pd.read_csv(file_path, encoding=self.encoding)
            self.stats['total_rows'] = len(df)
            
            # Handle missing values appropriately
            df = df.fillna('')
            
            return df.to_dict('records')
            
        except UnicodeDecodeError:
            # Fallback to different encodings
            for encoding in ['latin-1', 'cp1252', 'iso-8859-1']:
                try:
                    df = pd.read_csv(file_path, encoding=encoding)
                    self.logger.warning(f"Used {encoding} encoding instead of {self.encoding}")
                    self.stats['total_rows'] = len(df)
                    return df.fillna('').to_dict('records')
                except:
                    continue
            raise ValueError("Could not decode file with any common encoding")
            
        except pd.errors.EmptyDataError:
            self.logger.warning(f"CSV file is empty: {file_path}")
            return []
    
    def _load_json(self, file_path: str) -> List[Dict[str, Any]]:
        """Load data from JSON file supporting multiple structures."""
        try:
            with open(file_path, 'r', encoding=self.encoding) as f:
                data = json.load(f)
            
            # Handle different JSON structures
            if isinstance(data, list):
                # Array of objects: [{"question": "...", "answer": "..."}, ...]
                self.stats['total_rows'] = len(data)
                return data
            elif isinstance(data, dict):
                # Object with array: {"data": [...]} or {"examples": [...]}
                for key in ['data', 'examples', 'items', 'records']:
                    if key in data and isinstance(data[key], list):
                        self.stats['total_rows'] = len(data[key])
                        return data[key]
                # Single object - wrap in list
                self.stats['total_rows'] = 1
                return [data]
            else:
                raise ValueError("JSON structure not supported")
                
        except json.JSONDecodeError as e:
            raise ValueError(f"Invalid JSON format: {str(e)}")
        except UnicodeDecodeError:
            # Retry with different encoding
            try:
                with open(file_path, 'r', encoding='latin-1') as f:
                    data = json.load(f)
                    self.logger.warning("Used latin-1 encoding for JSON file")
                    return data if isinstance(data, list) else [data]
            except:
                raise ValueError("Could not decode JSON file")
    
    def _load_tsv(self, file_path: str) -> List[Dict[str, Any]]:
        """Load data from TSV file."""
        try:
            df = pd.read_csv(file_path, sep='\t', encoding=self.encoding)
            self.stats['total_rows'] = len(df)
            return df.fillna('').to_dict('records')
        except Exception as e:
            # Fallback to manual parsing
            return self._manual_tsv_parse(file_path)
    
    def _manual_tsv_parse(self, file_path: str) -> List[Dict[str, Any]]:
        """Manual TSV parsing as fallback."""
        try:
            with open(file_path, 'r', encoding=self.encoding) as f:
                reader = csv.DictReader(f, delimiter='\t')
                data = list(reader)
                self.stats['total_rows'] = len(data)
                return data
        except Exception as e:
            raise ValueError(f"Could not parse TSV file: {str(e)}")
    
    def _process_data(self, raw_data: List[Dict[str, Any]]) -> List[Dict[str, Any]]:
        """Process raw data: apply field mappings and quality checks."""
        processed = []
        
        for i, row in enumerate(raw_data):
            try:
                # Apply field mappings
                mapped_row = self._apply_field_mapping(row)
                
                # Validate data quality
                if self._validate_row(mapped_row, i):
                    processed.append(mapped_row)
                    self.stats['valid_examples'] += 1
                else:
                    self.stats['filtered_examples'] += 1
                    
            except Exception as e:
                self.stats['errors'].append(f"Row {i}: {str(e)}")
                self.stats['filtered_examples'] += 1
                
        return processed
    
    def _apply_field_mapping(self, row: Dict[str, Any]) -> Dict[str, Any]:
        """Apply field name mappings to standardize the data."""
        mapped = {}
        
        for source_field, value in row.items():
            # Apply mapping if exists, otherwise keep original name
            target_field = self.field_mapping.get(source_field, source_field)
            mapped[target_field] = value
            
            if source_field in self.field_mapping:
                self.stats['field_mappings_applied'] += 1
                
        return mapped
    
    def _validate_row(self, row: Dict[str, Any], row_index: int) -> bool:
        """Validate individual row meets quality standards."""
        
        # Check required fields exist
        for field in self.required_fields:
            if field not in row:
                self.logger.warning(f"Row {row_index}: Missing required field '{field}'")
                return False
        
        # Check for empty values in required fields
        for field in self.required_fields:
            value = str(row[field]).strip()
            if not value or value.lower() in ['nan', 'null', 'none']:
                self.logger.warning(f"Row {row_index}: Empty value in required field '{field}'")
                return False
        
        # Additional quality checks
        question = str(row.get('question', '')).strip()
        answer = str(row.get('answer', '')).strip()
        
        # Minimum length requirements
        if len(question) < 3:
            self.logger.warning(f"Row {row_index}: Question too short")
            return False
            
        if len(answer) < 1:
            self.logger.warning(f"Row {row_index}: Answer too short")
            return False
        
        return True
    
    def _create_dspy_examples(self, processed_data: List[Dict[str, Any]], 
                            input_fields: Optional[List[str]] = None) -> List[dspy.Example]:
        """Convert processed data to DSPy Examples with proper input marking."""
        
        examples = []
        
        # Default input fields if not specified
        if input_fields is None:
            # Infer input fields - typically everything except 'answer'
            sample_row = processed_data[0] if processed_data else {}
            input_fields = [field for field in sample_row.keys() 
                          if field not in ['answer', 'response', 'label', 'target']]
        
        for row in processed_data:
            try:
                # Create DSPy Example
                example = dspy.Example(**row)
                
                # Mark input fields appropriately
                if input_fields:
                    example = example.with_inputs(*input_fields)
                
                examples.append(example)
                
            except Exception as e:
                self.stats['errors'].append(f"Failed to create Example: {str(e)}")
                self.stats['filtered_examples'] += 1
        
        return examples
    
    def _log_loading_summary(self, file_path: str) -> None:
        """Generate comprehensive loading report."""
        self.logger.info("=" * 60)
        self.logger.info(f"DATASET LOADING SUMMARY: {os.path.basename(file_path)}")
        self.logger.info("=" * 60)
        self.logger.info(f"Total rows processed: {self.stats['total_rows']}")
        self.logger.info(f"Valid examples created: {self.stats['valid_examples']}")
        self.logger.info(f"Examples filtered out: {self.stats['filtered_examples']}")
        self.logger.info(f"Field mappings applied: {self.stats['field_mappings_applied']}")
        
        if self.stats['errors']:
            self.logger.warning(f"Errors encountered: {len(self.stats['errors'])}")
            for error in self.stats['errors'][:5]:  # Show first 5 errors
                self.logger.warning(f"  - {error}")
            if len(self.stats['errors']) > 5:
                self.logger.warning(f"  ... and {len(self.stats['errors']) - 5} more")
        
        success_rate = (self.stats['valid_examples'] / self.stats['total_rows']) * 100 if self.stats['total_rows'] > 0 else 0
        self.logger.info(f"Success rate: {success_rate:.1f}%")
        self.logger.info("=" * 60)
    
    def _reset_stats(self) -> None:
        """Reset statistics for new loading operation."""
        self.stats = {
            'total_rows': 0,
            'valid_examples': 0,
            'filtered_examples': 0,
            'errors': [],
            'field_mappings_applied': 0
        }

# Usage Examples and Testing
if __name__ == "__main__":
    # Initialize loader with custom field mappings
    loader = DatasetLoader(
        field_mapping={
            'query': 'question',
            'response': 'answer', 
            'passage': 'context'
        },
        required_fields=['question', 'answer']
    )
    
    # Test with different file formats
    try:
        # Load CSV dataset
        csv_dataset = loader.load_dataset('qa_data.csv', input_fields=['question', 'context'])
        print(f"CSV: Loaded {len(csv_dataset)} examples")
        
        # Load JSON dataset
        json_dataset = loader.load_dataset('qa_data.json', input_fields=['question'])
        print(f"JSON: Loaded {len(json_dataset)} examples")
        
        # Display sample data
        if csv_dataset:
            print("\\nSample example:")
            print(f"Inputs: {csv_dataset[0].inputs()}")
            print(f"Labels: {csv_dataset[0].labels()}")
        
    except Exception as e:
        print(f"Loading failed: {e}")`,
      testCases: [
        {
          input: 'class DatasetLoader',
          expected: true,
          description: 'Implements DatasetLoader class',
        },
        {
          input: 'field_mapping',
          expected: true,
          description: 'Handles field mapping',
        },
      ],
    },
    {
      id: 'dspy-m3-ex3',
      title: 'Build an AI-Powered Content Evaluation System',
      description:
        'Create a sophisticated evaluation framework that uses AI to assess multiple dimensions of content quality',
      difficulty: 'hard',
      type: 'implementation',
      instructions: `## Context
You're working for a content intelligence company that provides automated content evaluation services to publishers and marketing agencies. The company processes thousands of articles and summaries daily, requiring accurate assessment across multiple quality dimensions that simple metrics like ROUGE can't capture.

## Your Goal
Build a comprehensive AI-powered evaluation framework that can:
1. **Multi-Dimensional Assessment**: Evaluate content across relevance, accuracy, clarity, engagement, and completeness
2. **Adaptive Evaluation**: Adjust evaluation criteria based on content type and target audience
3. **Detailed Feedback**: Provide specific, actionable insights for improvement
4. **Confidence Scoring**: Indicate how confident the AI evaluator is in its assessments

## Requirements
- Create evaluation signatures for each quality dimension
- Use DSPy's AI capabilities to provide human-like judgment with detailed reasoning
- Handle different content types (summaries, articles, reports)
- Provide confidence scores and actionable feedback recommendations
- Test with various content scenarios

## Test Scenarios
- News article summary evaluation
- Technical documentation assessment  
- Creative content quality review
- Educational material effectiveness

## Starting Point
Think about how an expert content editor evaluates text. They consider not just what's said, but how it's said, who the audience is, and what the content aims to achieve.`,
      hints: [
        'Design signature-based approach: Create DSPy signatures for each evaluation dimension (relevance, accuracy, clarity, etc.)',
        'Use ChainOfThought modules to ensure the AI provides detailed reasoning for each assessment.',
        'Implement a ContentEvaluator class that orchestrates multiple evaluation dimensions.',
        'Add content type detection to adjust evaluation criteria appropriately.',
        "Include confidence scoring - the AI should indicate when it's uncertain about assessments.",
        'Create validation methods to catch common AI evaluation errors.',
        'Add detailed feedback generation - not just scores, but specific improvement recommendations.',
      ],
      solution: `import dspy
from typing import Dict, List, Optional
from dataclasses import dataclass
from enum import Enum

class ContentType(Enum):
    """Different types of content requiring different evaluation approaches."""
    SUMMARY = "summary"
    ARTICLE = "article" 
    TECHNICAL = "technical"
    CREATIVE = "creative"
    EDUCATIONAL = "educational"

@dataclass
class EvaluationResult:
    """Comprehensive evaluation result with scores and feedback."""
    overall_score: float
    dimension_scores: Dict[str, float]
    confidence: float
    reasoning: str
    feedback: List[str]
    content_type: ContentType

class ContentTypeDetector(dspy.Signature):
    """Detect the type and characteristics of content."""
    text: str = dspy.InputField(desc="Content to analyze")
    word_count: int = dspy.InputField(desc="Length of the content")
    
    content_type: str = dspy.OutputField(desc="Primary content type: summary, article, technical, creative, or educational")
    target_audience: str = dspy.OutputField(desc="Intended audience: general, professional, academic, or technical")
    reasoning: str = dspy.OutputField(desc="Brief explanation of content type determination")

class RelevanceEvaluator(dspy.Signature):
    """Evaluate how well content addresses its intended purpose."""
    original_text: str = dspy.InputField(desc="Original source material or context")
    content: str = dspy.InputField(desc="Content to evaluate")
    content_type: str = dspy.InputField(desc="Type of content being evaluated")
    
    relevance_score: float = dspy.OutputField(desc="0-1 score for content relevance")
    key_points_covered: float = dspy.OutputField(desc="0-1 score for coverage of important points")
    reasoning: str = dspy.OutputField(desc="Detailed explanation of relevance assessment")

class AccuracyEvaluator(dspy.Signature):
    """Evaluate factual accuracy and consistency."""
    original_text: str = dspy.InputField(desc="Source material for fact-checking")
    content: str = dspy.InputField(desc="Content to evaluate for accuracy")
    
    factual_accuracy: float = dspy.OutputField(desc="0-1 score for factual correctness")
    consistency: float = dspy.OutputField(desc="0-1 score for internal consistency")
    confidence: float = dspy.OutputField(desc="0-1 confidence in accuracy assessment")
    reasoning: str = dspy.OutputField(desc="Explanation of accuracy evaluation")

class ContentEvaluator:
    """
    Comprehensive AI-powered content evaluation system.
    
    Provides multi-dimensional assessment with detailed feedback
    and confidence scoring for production content evaluation.
    """
    
    def __init__(self, evaluation_weights: Optional[Dict[str, float]] = None):
        """Initialize the content evaluator."""
        # Default weights optimized for general content evaluation
        self.weights = evaluation_weights or {
            'relevance': 0.4,
            'accuracy': 0.3, 
            'clarity': 0.3
        }
        
        # Initialize evaluation modules
        self.content_detector = dspy.ChainOfThought(ContentTypeDetector)
        self.relevance_eval = dspy.ChainOfThought(RelevanceEvaluator)
        self.accuracy_eval = dspy.ChainOfThought(AccuracyEvaluator)
    
    def evaluate_content(self, original_text: str, content: str, 
                        content_type: Optional[ContentType] = None) -> EvaluationResult:
        """
        Perform comprehensive content evaluation.
        
        Args:
            original_text: Source material or context
            content: Content to evaluate
            content_type: Optional content type override
            
        Returns:
            Detailed evaluation result with scores and feedback
        """
        # Detect content characteristics if not provided
        if content_type is None:
            detection = self.content_detector(
                text=content[:500],  # Analyze first 500 chars
                word_count=len(content.split())
            )
            content_type_str = detection.content_type
            target_audience = detection.target_audience
        else:
            content_type_str = content_type.value
            target_audience = "general"  # Default fallback
        
        # Perform multi-dimensional evaluation
        dimension_scores = {}
        all_reasoning = []
        confidence_scores = []
        
        try:
            # 1. Relevance Evaluation
            relevance_result = self.relevance_eval(
                original_text=original_text,
                content=content,
                content_type=content_type_str
            )
            dimension_scores['relevance'] = relevance_result.relevance_score
            all_reasoning.append(f"Relevance: {relevance_result.reasoning}")
            
            # 2. Accuracy Evaluation
            accuracy_result = self.accuracy_eval(
                original_text=original_text,
                content=content
            )
            dimension_scores['accuracy'] = accuracy_result.factual_accuracy
            confidence_scores.append(accuracy_result.confidence)
            all_reasoning.append(f"Accuracy: {accuracy_result.reasoning}")
            
        except Exception as e:
            # Graceful fallback for evaluation errors
            dimension_scores = {dim: 0.5 for dim in self.weights.keys()}
            all_reasoning = [f"Evaluation error: {str(e)}"]
            confidence_scores = [0.3]  # Low confidence due to errors
        
        # Calculate overall score
        overall_score = sum(
            dimension_scores.get(dim, 0.5) * weight 
            for dim, weight in self.weights.items()
        )
        
        # Calculate confidence
        avg_confidence = sum(confidence_scores) / len(confidence_scores) if confidence_scores else 0.7
        
        # Generate feedback
        feedback = self._generate_feedback(content, dimension_scores, content_type_str)
        
        return EvaluationResult(
            overall_score=overall_score,
            dimension_scores=dimension_scores,
            confidence=avg_confidence,
            reasoning="; ".join(all_reasoning),
            feedback=feedback,
            content_type=ContentType(content_type_str)
        )
    
    def _generate_feedback(self, content: str, scores: Dict[str, float], 
                          content_type: str) -> List[str]:
        """Generate actionable feedback based on evaluation results."""
        feedback = []
        for dim, score in scores.items():
            if score < 0.6:
                feedback.append(f"Consider improving {dim} (current score: {score:.2f})")
        return feedback or ["Overall content quality is acceptable"]

# DSPy metric integration
def ai_content_metric(example, pred, trace=None):
    """DSPy-compatible metric using the advanced content evaluator."""
    evaluator = ContentEvaluator()
    
    # Get source text and prediction
    original_text = getattr(example, 'text', getattr(example, 'document', ''))
    predicted_content = getattr(pred, 'summary', getattr(pred, 'content', getattr(pred, 'answer', '')))
    
    # Perform evaluation
    result = evaluator.evaluate_content(original_text, predicted_content)
    
    if trace is None:
        # Evaluation mode - return overall score
        return result.overall_score
    else:
        # Optimization mode - stricter requirements
        return (
            result.overall_score > 0.7 and 
            result.confidence > 0.6 and
            all(score > 0.5 for score in result.dimension_scores.values())
        )

# Usage example and testing
if __name__ == "__main__":
    # Initialize the evaluator
    evaluator = ContentEvaluator()
    
    # Test content evaluation
    original_article = """
    Climate change represents one of the most significant challenges of our time. 
    Rising global temperatures, caused primarily by greenhouse gas emissions from human activities,
    are leading to widespread environmental impacts including melting ice caps, rising sea levels,
    and more frequent extreme weather events.
    """
    
    summary_to_evaluate = """
    Climate change is a major global challenge caused by human greenhouse gas emissions,
    resulting in rising temperatures and environmental impacts like melting ice and extreme weather.
    """
    
    # Perform evaluation
    result = evaluator.evaluate_content(original_article, summary_to_evaluate)
    
    # Display results
    print(f"Overall Score: {result.overall_score:.3f}")
    print(f"Confidence: {result.confidence:.3f}")
    print(f"Content Type: {result.content_type.value}")
    print(f"\\nFeedback:")
    for feedback_item in result.feedback:
        print(f"  - {feedback_item}")`,
      testCases: [
        {
          input: 'class ContentEvaluator',
          expected: true,
          description: 'Implements comprehensive content evaluator',
        },
        {
          input: 'dspy.ChainOfThought',
          expected: true,
          description: 'Uses ChainOfThought for reasoning',
        },
      ],
    },
    {
      id: 'dspy-m3-ex4',
      title: 'Build a Comprehensive Evaluation Platform for QA Systems',
      description:
        'Create a production-ready evaluation framework that compares multiple QA approaches across diverse metrics and provides actionable insights for system selection and optimization',
      difficulty: 'hard',
      type: 'implementation',
      instructions: `## Context
You're the Lead AI Engineer at an enterprise software company that provides question-answering services to multiple client organizations. Each client has different requirements - some need fast, simple answers for customer support, others require detailed explanations for educational content, and some need highly accurate responses backed by verified sources.

The company currently uses a basic QA system, but the product team wants to evaluate whether more sophisticated approaches (Chain of Thought reasoning, Retrieval-Augmented Generation) would better serve different client segments. They need a comprehensive evaluation platform that can:
- Compare multiple QA architectures objectively
- Analyze performance across different question types and domains
- Provide clear recommendations for which approach works best for which use cases
- Generate detailed reports for stakeholders and clients

## Your Goal
Build a sophisticated evaluation platform that demonstrates enterprise-level AI system comparison:

1. **Multi-Architecture Support**: Implement and compare three distinct QA approaches with different strengths and trade-offs
2. **Comprehensive Metrics**: Evaluate across multiple dimensions including accuracy, reasoning quality, response appropriateness, and computational efficiency
3. **Detailed Analysis**: Provide statistical analysis, performance breakdowns by question type, and confidence intervals
4. **Production Insights**: Generate actionable recommendations about when to use each approach
5. **Scalable Design**: Create a framework that can easily accommodate new QA systems and evaluation metrics

## Requirements
- Create three production-quality QA modules with different architectures
- Implement a comprehensive suite of evaluation metrics (accuracy, semantic similarity, reasoning quality, efficiency)
- Build statistical analysis capabilities including confidence intervals and significance testing
- Provide detailed performance breakdowns by question type, difficulty, and domain
- Generate executive-level summary reports with clear recommendations
- Include error analysis and failure mode identification
- Handle edge cases and provide robust error handling throughout

## Test Dataset Requirements
Create and evaluate on diverse question types:
- **Factual Questions**: "What is the capital of Australia?"
- **Reasoning Questions**: "If a train travels 60 mph for 2.5 hours, how far does it go?"
- **Contextual Questions**: "Given this product review, what are the main complaints?"
- **Complex Analysis**: "What are the potential economic impacts of renewable energy adoption?"

## Success Criteria
Your implementation should demonstrate:
- Clear performance differences between QA approaches
- Statistical significance in comparisons
- Actionable insights about when to use each approach
- Professional-quality reporting suitable for business stakeholders
- Robust handling of diverse question types and edge cases

## Starting Point
Think about how a data science team would approach comparing ML models in production. You need both rigorous statistical analysis and clear business insights. Consider the trade-offs each QA approach makes and how different metrics might favor different approaches.

The goal is not just to declare one system "best" but to understand the nuanced performance characteristics that guide real-world deployment decisions.`,
      hints: [
        'Start with a class-based architecture: class QAEvaluationPlatform that orchestrates the entire comparison process.',
        'Design three distinct QA modules: BasicQA (fast/simple), ReasoningQA (chain-of-thought), and KnowledgeQA (RAG-based).',
        'Create a comprehensive MetricSuite class that handles multiple evaluation dimensions.',
        'Implement statistical analysis: confidence intervals, significance testing, effect sizes.',
        'Use stratified evaluation - group results by question type, difficulty, domain for detailed analysis.',
        'Add performance profiling - measure not just accuracy but also response time and token usage.',
        'Create a ReportGenerator class that produces executive summaries and technical details.',
        'Include error analysis methods to identify failure patterns and edge cases.',
        'Add visualization capabilities for performance comparisons and trend analysis.',
        'Design the system to be extensible - easy to add new QA systems or metrics.',
      ],
      solution: `import dspy
from dspy.evaluate import Evaluate
import time
import statistics
from typing import Dict, List, Optional, Tuple, Any
from dataclasses import dataclass
from collections import defaultdict
import json

@dataclass
class EvaluationResult:
    """Comprehensive evaluation results for a single QA system."""
    system_name: str
    overall_scores: Dict[str, float]
    question_type_scores: Dict[str, Dict[str, float]]
    performance_metrics: Dict[str, float]
    confidence_intervals: Dict[str, Tuple[float, float]]
    error_analysis: Dict[str, Any]

@dataclass
class ComparisonReport:
    """Executive summary of QA system comparison."""
    best_overall: str
    best_by_category: Dict[str, str]
    recommendations: List[str]
    detailed_results: Dict[str, EvaluationResult]
    statistical_significance: Dict[str, bool]

class BasicQA(dspy.Module):
    """
    Simple, fast QA system optimized for quick responses.
    Best for: Customer support, simple factual questions.
    """
    
    def __init__(self):
        super().__init__()
        self.qa = dspy.Predict("question -> answer")
        self.system_name = "Basic QA"
        self.description = "Direct question-to-answer mapping, optimized for speed"
    
    def forward(self, question):
        return self.qa(question=question)

class ReasoningQA(dspy.Module):
    """
    Chain-of-thought QA system that shows reasoning process.
    Best for: Educational content, complex reasoning, transparency.
    """
    
    def __init__(self):
        super().__init__()
        self.qa = dspy.ChainOfThought("question -> reasoning, answer")
        self.system_name = "Reasoning QA"
        self.description = "Chain-of-thought reasoning for complex questions"
    
    def forward(self, question):
        return self.qa(question=question)

class KnowledgeQA(dspy.Module):
    """
    RAG-based QA system with external knowledge retrieval.
    Best for: Knowledge-intensive questions, factual accuracy, citations.
    """
    
    def __init__(self, k=5):
        super().__init__()
        self.retrieve = dspy.Retrieve(k=k)
        self.generate = dspy.ChainOfThought("context, question -> answer, citations")
        self.system_name = "Knowledge QA"
        self.description = "Retrieval-augmented generation with source citations"
    
    def forward(self, question):
        try:
            passages = self.retrieve(question).passages
            return self.generate(context=passages, question=question)
        except:
            # Fallback to direct generation if retrieval fails
            fallback = dspy.ChainOfThought("question -> answer")
            return fallback(question=question)

class MetricSuite:
    """
    Comprehensive evaluation metrics for QA systems.
    Evaluates accuracy, reasoning quality, efficiency, and appropriateness.
    """
    
    def __init__(self):
        self.metrics = {
            'exact_match': self._exact_match,
            'semantic_similarity': self._semantic_similarity,
            'answer_relevance': self._answer_relevance,
            'reasoning_quality': self._reasoning_quality,
            'response_completeness': self._response_completeness
        }
    
    def _exact_match(self, example, pred, trace=None):
        """Strict exact match evaluation."""
        gold = example.answer.lower().strip()
        predicted = getattr(pred, 'answer', str(pred)).lower().strip()
        return gold == predicted
    
    def _semantic_similarity(self, example, pred, trace=None):
        """Semantic similarity using containment and overlap."""
        gold = example.answer.lower()
        predicted = getattr(pred, 'answer', str(pred)).lower()
        
        # Check bidirectional containment
        if gold in predicted or predicted in gold:
            return 1.0
        
        # Word overlap scoring
        gold_words = set(gold.split())
        pred_words = set(predicted.split())
        
        if not gold_words or not pred_words:
            return 0.0
        
        overlap = len(gold_words & pred_words)
        union = len(gold_words | pred_words)
        
        return overlap / union if union > 0 else 0.0
    
    def _answer_relevance(self, example, pred, trace=None):
        """Check if answer is relevant to the question."""
        question_words = set(example.question.lower().split())
        answer_words = set(getattr(pred, 'answer', str(pred)).lower().split())
        
        # Simple relevance check based on word overlap and length
        overlap = len(question_words & answer_words)
        answer_length = len(getattr(pred, 'answer', str(pred)).split())
        
        # Penalize very short or very long answers
        length_score = 1.0
        if answer_length < 2:
            length_score = 0.3
        elif answer_length > 100:
            length_score = 0.7
        
        # Combine overlap and length considerations
        relevance = (overlap / len(question_words) + length_score) / 2
        return min(relevance, 1.0)
    
    def _reasoning_quality(self, example, pred, trace=None):
        """Evaluate quality of reasoning (if present)."""
        if not hasattr(pred, 'reasoning'):
            return 0.5  # Neutral score for systems without reasoning
        
        reasoning = pred.reasoning
        if not reasoning or len(reasoning) < 10:
            return 0.0
        
        # Basic reasoning quality checks
        quality_score = 0.5  # Base score
        
        # Bonus for structured reasoning
        if any(word in reasoning.lower() for word in ['because', 'therefore', 'first', 'then']):
            quality_score += 0.3
        
        # Bonus for appropriate length
        if 50 <= len(reasoning) <= 300:
            quality_score += 0.2
        
        return min(quality_score, 1.0)
    
    def _response_completeness(self, example, pred, trace=None):
        """Evaluate if response fully addresses the question."""
        question = example.question.lower()
        answer = getattr(pred, 'answer', str(pred)).lower()
        
        # Check for question type and appropriate response
        if 'what' in question and len(answer.split()) >= 2:
            return 0.8
        elif 'how' in question and len(answer.split()) >= 5:
            return 0.9
        elif 'why' in question and len(answer.split()) >= 8:
            return 0.9
        elif 'when' in question or 'where' in question:
            return 0.7 if len(answer.split()) >= 1 else 0.3
        else:
            return 0.6  # Default score for other question types

class QAEvaluationPlatform:
    """
    Comprehensive platform for evaluating and comparing QA systems.
    
    Provides enterprise-level analysis capabilities including statistical
    testing, performance profiling, and executive reporting.
    """
    
    def __init__(self):
        """Initialize the evaluation platform with QA systems and metrics."""
        # Initialize QA systems to compare
        self.qa_systems = {
            'Basic QA': BasicQA(),
            'Reasoning QA': ReasoningQA(),
            'Knowledge QA': KnowledgeQA(k=3)
        }
        
        # Initialize comprehensive metrics
        self.metric_suite = MetricSuite()
        
        # Results storage
        self.evaluation_results = {}
        self.performance_data = defaultdict(list)
    
    def evaluate_systems(self, dataset: List[dspy.Example], 
                        num_threads: int = 4) -> ComparisonReport:
        """
        Comprehensive evaluation of all QA systems.
        
        Args:
            dataset: List of DSPy examples with questions and expected answers
            num_threads: Number of parallel threads for evaluation
            
        Returns:
            Detailed comparison report with recommendations
        """
        print("🚀 Starting Comprehensive QA System Evaluation")
        print("=" * 60)
        
        # Evaluate each system
        for system_name, qa_system in self.qa_systems.items():
            print(f"\\n📊 Evaluating {system_name}...")
            result = self._evaluate_single_system(
                qa_system, dataset, system_name, num_threads
            )
            self.evaluation_results[system_name] = result
        
        # Generate comprehensive comparison report
        report = self._generate_comparison_report()
        
        # Display results
        self._display_results(report)
        
        return report
    
    def _evaluate_single_system(self, qa_system, dataset: List[dspy.Example], 
                               system_name: str, num_threads: int) -> EvaluationResult:
        """Evaluate a single QA system across all metrics."""
        
        # Evaluate across all metrics
        overall_scores = {}
        question_type_scores = defaultdict(lambda: defaultdict(list))
        
        for metric_name, metric_fn in self.metric_suite.metrics.items():
            print(f"  • Testing {metric_name}...")
            
            # Measure performance
            start_time = time.time()
            
            evaluator = Evaluate(
                devset=dataset,
                metric=metric_fn,
                num_threads=num_threads,
                display_progress=False,
                display_table=0
            )
            
            score = evaluator(qa_system)
            evaluation_time = time.time() - start_time
            
            overall_scores[metric_name] = score
            
            # Track performance metrics
            self.performance_data[system_name].append({
                'metric': metric_name,
                'score': score,
                'time': evaluation_time
            })
            
            # Detailed analysis by question type (if available)
            for example in dataset:
                if hasattr(example, 'question_type'):
                    pred = qa_system(question=example.question)
                    type_score = metric_fn(example, pred)
                    question_type_scores[example.question_type][metric_name].append(type_score)
        
        # Calculate confidence intervals
        confidence_intervals = self._calculate_confidence_intervals(overall_scores)
        
        # Performance metrics
        avg_time = statistics.mean([p['time'] for p in self.performance_data[system_name]])
        performance_metrics = {
            'avg_evaluation_time': avg_time,
            'total_metrics': len(self.metric_suite.metrics),
            'dataset_size': len(dataset)
        }
        
        # Error analysis
        error_analysis = self._analyze_errors(qa_system, dataset)
        
        # Aggregate question type scores
        final_type_scores = {}
        for q_type, metrics in question_type_scores.items():
            final_type_scores[q_type] = {
                metric: statistics.mean(scores) 
                for metric, scores in metrics.items()
            }
        
        return EvaluationResult(
            system_name=system_name,
            overall_scores=overall_scores,
            question_type_scores=final_type_scores,
            performance_metrics=performance_metrics,
            confidence_intervals=confidence_intervals,
            error_analysis=error_analysis
        )
    
    def _calculate_confidence_intervals(self, scores: Dict[str, float]) -> Dict[str, Tuple[float, float]]:
        """Calculate 95% confidence intervals for scores."""
        # Simplified confidence intervals (in production, would use bootstrap sampling)
        confidence_intervals = {}
        for metric, score in scores.items():
            margin = 0.05  # Simplified margin of error
            confidence_intervals[metric] = (
                max(0.0, score - margin),
                min(1.0, score + margin)
            )
        return confidence_intervals
    
    def _analyze_errors(self, qa_system, dataset: List[dspy.Example]) -> Dict[str, Any]:
        """Analyze failure patterns and common errors."""
        failures = []
        
        for example in dataset[:10]:  # Sample analysis
            try:
                pred = qa_system(question=example.question)
                exact_match = self.metric_suite._exact_match(example, pred)
                
                if not exact_match:
                    failures.append({
                        'question': example.question,
                        'expected': example.answer,
                        'predicted': getattr(pred, 'answer', str(pred)),
                        'question_type': getattr(example, 'question_type', 'unknown')
                    })
            except Exception as e:
                failures.append({
                    'question': example.question,
                    'error': str(e),
                    'type': 'system_error'
                })
        
        return {
            'failure_count': len(failures),
            'failure_rate': len(failures) / min(len(dataset), 10),
            'sample_failures': failures[:3]  # Show first 3 failures
        }
    
    def _generate_comparison_report(self) -> ComparisonReport:
        """Generate comprehensive comparison report with recommendations."""
        
        # Find best overall system
        overall_averages = {}
        for system_name, result in self.evaluation_results.items():
            avg_score = statistics.mean(result.overall_scores.values())
            overall_averages[system_name] = avg_score
        
        best_overall = max(overall_averages, key=overall_averages.get)
        
        # Find best by category
        best_by_category = {}
        all_metrics = list(self.metric_suite.metrics.keys())
        
        for metric in all_metrics:
            metric_scores = {
                system: result.overall_scores[metric]
                for system, result in self.evaluation_results.items()
            }
            best_by_category[metric] = max(metric_scores, key=metric_scores.get)
        
        # Generate recommendations
        recommendations = self._generate_recommendations(overall_averages, best_by_category)
        
        # Statistical significance (simplified)
        statistical_significance = {}
        for metric in all_metrics:
            scores = [result.overall_scores[metric] for result in self.evaluation_results.values()]
            # Simplified significance test (difference > 0.1)
            significance = max(scores) - min(scores) > 0.1
            statistical_significance[metric] = significance
        
        return ComparisonReport(
            best_overall=best_overall,
            best_by_category=best_by_category,
            recommendations=recommendations,
            detailed_results=self.evaluation_results,
            statistical_significance=statistical_significance
        )
    
    def _generate_recommendations(self, overall_averages: Dict[str, float], 
                                 best_by_category: Dict[str, str]) -> List[str]:
        """Generate actionable recommendations for system selection."""
        recommendations = []
        
        # Overall recommendation
        best_system = max(overall_averages, key=overall_averages.get)
        recommendations.append(
            f"🏆 Overall Best: {best_system} (avg score: {overall_averages[best_system]:.3f})"
        )
        
        # Specific use case recommendations
        if best_by_category.get('exact_match') == 'Knowledge QA':
            recommendations.append(
                "📚 Use Knowledge QA for factual accuracy and when citations are important"
            )
        
        if best_by_category.get('reasoning_quality') == 'Reasoning QA':
            recommendations.append(
                "🧠 Use Reasoning QA for educational content where explanation is valued"
            )
        
        if best_by_category.get('answer_relevance') == 'Basic QA':
            recommendations.append(
                "⚡ Use Basic QA for high-volume, simple questions requiring fast responses"
            )
        
        # Performance recommendations
        fastest_system = min(
            self.evaluation_results.keys(),
            key=lambda x: self.evaluation_results[x].performance_metrics['avg_evaluation_time']
        )
        recommendations.append(
            f"🚀 Fastest System: {fastest_system} for high-throughput scenarios"
        )
        
        return recommendations
    
    def _display_results(self, report: ComparisonReport) -> None:
        """Display comprehensive evaluation results."""
        print("\\n" + "=" * 80)
        print("🎯 COMPREHENSIVE QA SYSTEM EVALUATION RESULTS")
        print("=" * 80)
        
        # Overall rankings
        print("\\n📊 OVERALL PERFORMANCE RANKINGS:")
        print("-" * 40)
        
        sorted_systems = sorted(
            report.detailed_results.items(),
            key=lambda x: statistics.mean(x[1].overall_scores.values()),
            reverse=True
        )
        
        for rank, (system_name, result) in enumerate(sorted_systems, 1):
            avg_score = statistics.mean(result.overall_scores.values())
            print(f"{rank}. {system_name}: {avg_score:.3f}")
        
        # Metric breakdown
        print("\\n📈 DETAILED METRIC BREAKDOWN:")
        print("-" * 40)
        
        metrics = list(self.metric_suite.metrics.keys())
        header = f"{'System':<15}" + "".join(f"{m[:8]:<10}" for m in metrics)
        print(header)
        print("-" * len(header))
        
        for system_name, result in report.detailed_results.items():
            row = f"{system_name:<15}"
            for metric in metrics:
                score = result.overall_scores[metric]
                row += f"{score:.3f}    "
            print(row)
        
        # Recommendations
        print("\\n🎯 STRATEGIC RECOMMENDATIONS:")
        print("-" * 40)
        for i, rec in enumerate(report.recommendations, 1):
            print(f"{i}. {rec}")
        
        # Statistical significance
        print("\\n📊 STATISTICAL SIGNIFICANCE:")
        print("-" * 40)
        for metric, significant in report.statistical_significance.items():
            status = "✅ Significant" if significant else "❌ Not significant"
            print(f"{metric}: {status}")
        
        print("\\n" + "=" * 80)

# Usage Example and Testing
if __name__ == "__main__":
    # Create comprehensive test dataset
    test_dataset = [
        # Factual questions
        dspy.Example(
            question="What is the capital of France?",
            answer="Paris",
            question_type="factual"
        ).with_inputs("question"),
        
        dspy.Example(
            question="When was the Declaration of Independence signed?",
            answer="1776",
            question_type="factual"
        ).with_inputs("question"),
        
        # Reasoning questions
        dspy.Example(
            question="If a train travels 60 mph for 2.5 hours, how far does it go?",
            answer="150 miles",
            question_type="reasoning"
        ).with_inputs("question"),
        
        dspy.Example(
            question="Why do seasons occur on Earth?",
            answer="Seasons occur because Earth's axis is tilted relative to its orbit around the Sun",
            question_type="reasoning"
        ).with_inputs("question"),
        
        # Complex questions
        dspy.Example(
            question="What are the main advantages of renewable energy?",
            answer="Renewable energy reduces carbon emissions, provides energy independence, and has lower long-term costs",
            question_type="complex"
        ).with_inputs("question"),
    ]
    
    # Initialize and run evaluation platform
    platform = QAEvaluationPlatform()
    
    print("🔬 Starting Enterprise QA System Evaluation...")
    print(f"📋 Dataset size: {len(test_dataset)} examples")
    print(f"🔧 Systems to evaluate: {list(platform.qa_systems.keys())}")
    print(f"📏 Metrics: {list(platform.metric_suite.metrics.keys())}")
    
    # Run comprehensive evaluation
    comparison_report = platform.evaluate_systems(test_dataset, num_threads=2)
    
    # Export results for further analysis
    results_summary = {
        'best_overall': comparison_report.best_overall,
        'metric_leaders': comparison_report.best_by_category,
        'recommendations': comparison_report.recommendations,
        'evaluation_timestamp': time.strftime('%Y-%m-%d %H:%M:%S')
    }
    
    print("\\n💾 Evaluation complete! Results exported for stakeholder review.")
    print(f"📧 Executive Summary: {json.dumps(results_summary, indent=2)}")`,
      testCases: [
        {
          input: 'class QAEvaluationPlatform',
          expected: true,
          description: 'Implements comprehensive evaluation platform',
        },
        {
          input: 'class MetricSuite',
          expected: true,
          description: 'Implements comprehensive metrics suite',
        },
        {
          input: 'ComparisonReport',
          expected: true,
          description: 'Generates detailed comparison reports',
        },
        {
          input: 'statistical_significance',
          expected: true,
          description: 'Includes statistical analysis',
        },
      ],
    },
  ],
  quiz: {
    id: 'dspy-m3-quiz',
    title: 'Module 3: Evaluation in DSPy - Knowledge Check',
    description: 'Test your understanding of evaluation concepts',
    passingScore: 7,
    questionsPerQuiz: 10,
    questions: [], // This will be populated from questionBank randomly
    questionBank: [
      // Data and Examples (Questions 1-20)
      {
        id: 'q1',
        question: 'How many examples are typically needed for effective DSPy optimization?',
        choices: ['1000-5000', '500-1000', '200-500', '20-200'],
        correctAnswer: 3,
        explanation: 'DSPy can work effectively with minimal data, often just 20-200 examples.',
      },
      {
        id: 'q2',
        question: 'What is the core data type in DSPy for training and evaluation?',
        choices: ['dspy.Dataset', 'dspy.Example', 'dspy.DataPoint', 'dspy.Sample'],
        correctAnswer: 1,
        explanation:
          'dspy.Example is the core data type that holds input-output pairs for training and evaluation.',
      },
      {
        id: 'q3',
        question: 'What does .with_inputs() do on a DSPy Example?',
        choices: [
          'Validates the input data',
          'Specifies which fields are inputs vs labels',
          'Converts inputs to embeddings',
          'Filters out invalid inputs',
        ],
        correctAnswer: 1,
        explanation:
          '.with_inputs() marks which fields are available at inference time vs expected outputs.',
      },
      {
        id: 'q4',
        question: 'Which is better for DSPy training: input-output pairs or input-only data?',
        choices: [
          'Input-only data is always better',
          'Input-output pairs are preferred when available',
          'Both are equally effective',
          'It depends on the model size',
        ],
        correctAnswer: 1,
        explanation:
          'Input-output pairs are preferred because they provide clear targets for optimization.',
      },
      {
        id: 'q5',
        question: "What happens if you don't specify input keys in DSPy Examples?",
        choices: [
          'DSPy automatically detects them',
          'The program fails immediately',
          'DSPy might incorrectly use labels during training',
          "Nothing, it's optional",
        ],
        correctAnswer: 2,
        explanation:
          'Without input keys, DSPy might incorrectly use label fields during training, causing data leakage.',
      },
      {
        id: 'q6',
        question: 'What is the recommended train/dev/test split for DSPy datasets?',
        choices: ['90/5/5', '80/10/10', '70/15/15', '60/20/20'],
        correctAnswer: 2,
        explanation:
          'A 70/15/15 split balances having enough training data with sufficient validation and test sets.',
      },
      {
        id: 'q7',
        question: 'Why does DSPy need far less data than traditional ML?',
        choices: [
          'It uses simpler models',
          'It leverages pre-trained language model knowledge',
          "It doesn't actually learn anything",
          'It memorizes the training data',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy leverages the existing knowledge in pre-trained LMs, needing data mainly for optimization.',
      },
      {
        id: 'q8',
        question: 'What type of data is most valuable for DSPy optimization?',
        choices: [
          'Large amounts of unlabeled data',
          'Diverse examples covering edge cases',
          'Perfectly formatted examples only',
          'Synthetic data generated by AI',
        ],
        correctAnswer: 1,
        explanation:
          'Diverse examples that cover different scenarios and edge cases lead to robust optimizations.',
      },
      {
        id: 'q9',
        question: 'How should you handle intermediate reasoning steps in DSPy data?',
        choices: [
          'Always include them as required fields',
          'Never include them',
          'Include them only if you have labels',
          'Generate them synthetically',
        ],
        correctAnswer: 2,
        explanation:
          'Include intermediate steps when you have labels, but DSPy can learn to generate them without.',
      },
      {
        id: 'q10',
        question: "What's the purpose of development (dev) data in DSPy?",
        choices: [
          'Training the language model',
          'Evaluating and iterating on programs',
          'Final performance reporting',
          'Generating synthetic examples',
        ],
        correctAnswer: 1,
        explanation:
          'Development data is used to evaluate and iterate on your programs during development.',
      },
      {
        id: 'q11',
        question: 'When collecting data for DSPy, what should you prioritize?',
        choices: [
          'Quantity over quality',
          'Perfect formatting',
          'Input diversity and output quality',
          'Only easy examples',
        ],
        correctAnswer: 2,
        explanation: 'Focus on diverse inputs and high-quality outputs rather than just volume.',
      },
      {
        id: 'q12',
        question: 'How does DSPy handle missing output fields during training?',
        choices: [
          'It crashes immediately',
          'It can learn to generate them',
          'It skips those examples',
          'It fills them with defaults',
        ],
        correctAnswer: 1,
        explanation: 'DSPy can learn to generate intermediate fields even without explicit labels.',
      },
      {
        id: 'q13',
        question: "What's a key advantage of using production logs for DSPy data?",
        choices: [
          "They're always perfectly formatted",
          'They capture real-world diversity',
          'They include all intermediate steps',
          "They're easier to collect",
        ],
        correctAnswer: 1,
        explanation:
          'Production logs capture real-world input diversity and edge cases users actually encounter.',
      },
      {
        id: 'q14',
        question: 'How should you version control DSPy datasets?',
        choices: [
          "Don't version them",
          'Use Git LFS or dedicated data versioning tools',
          'Keep them in regular Git',
          'Store only the latest version',
        ],
        correctAnswer: 1,
        explanation: 'Use Git LFS or tools like DVC for versioning larger datasets efficiently.',
      },
      {
        id: 'q15',
        question: "What's the impact of imbalanced data on DSPy programs?",
        choices: [
          'No impact at all',
          'Programs may bias toward common cases',
          'Training will fail',
          'Evaluation becomes impossible',
        ],
        correctAnswer: 1,
        explanation:
          'Imbalanced data can cause programs to perform poorly on underrepresented cases.',
      },
      {
        id: 'q16',
        question: 'When should you use stratified sampling in DSPy?',
        choices: [
          'Never',
          'Always',
          'When you have distinct categories to preserve',
          'Only for test data',
        ],
        correctAnswer: 2,
        explanation:
          'Use stratified sampling to ensure all categories are represented in train/dev/test splits.',
      },
      {
        id: 'q17',
        question: 'What makes a good DSPy Example for QA tasks?',
        choices: [
          'Very long questions',
          'Clear question with accurate answer',
          'Multiple possible answers',
          'Ambiguous questions',
        ],
        correctAnswer: 1,
        explanation: 'Good QA examples have clear questions with accurate, verifiable answers.',
      },
      {
        id: 'q18',
        question: 'How do you handle multi-modal data in DSPy?',
        choices: [
          "DSPy doesn't support it",
          'Convert everything to text descriptions',
          'Use specialized multi-modal modules',
          'Store images separately',
        ],
        correctAnswer: 1,
        explanation:
          'Currently, DSPy works with text, so multi-modal data needs text descriptions.',
      },
      {
        id: 'q19',
        question: "What's the benefit of synthetic data augmentation in DSPy?",
        choices: [
          "It's always better than real data",
          'It can help cover edge cases',
          "It's required for optimization",
          'It reduces the need for evaluation',
        ],
        correctAnswer: 1,
        explanation:
          'Synthetic augmentation can help cover edge cases and increase training diversity.',
      },
      {
        id: 'q20',
        question: 'How should you handle PII in DSPy datasets?',
        choices: [
          'Include it as-is',
          'Remove or anonymize before use',
          'DSPy handles it automatically',
          'Only remove names',
        ],
        correctAnswer: 1,
        explanation: 'Always remove or properly anonymize PII before using data in DSPy.',
      },

      // Metrics and Evaluation (Questions 21-40)
      {
        id: 'q21',
        question: 'What is the purpose of dspy.Evaluate?',
        choices: [
          'To optimize DSPy programs',
          'To systematically measure program performance',
          'To debug DSPy modules',
          'To configure language models',
        ],
        correctAnswer: 1,
        explanation:
          'dspy.Evaluate systematically measures how well your DSPy program performs on a dataset.',
      },
      {
        id: 'q22',
        question: 'What does the trace parameter in evaluation metrics provide?',
        choices: [
          'Performance timing data',
          'Debugging information about the prediction process',
          'Model configuration details',
          'Input validation results',
        ],
        correctAnswer: 1,
        explanation:
          'The trace parameter provides debugging information about how the prediction was made.',
      },
      {
        id: 'q23',
        question: 'What should a DSPy metric function return?',
        choices: [
          'A string description',
          'A boolean (pass/fail)',
          'A dictionary of scores',
          'The prediction object',
        ],
        correctAnswer: 1,
        explanation:
          'Metrics should return boolean values indicating whether the prediction passes.',
      },
      {
        id: 'q24',
        question: "What's the advantage of semantic similarity metrics over exact match?",
        choices: [
          "They're faster to compute",
          'They handle paraphrases and variations',
          "They're more strict",
          'They require less data',
        ],
        correctAnswer: 1,
        explanation: 'Semantic similarity can recognize correct answers expressed differently.',
      },
      {
        id: 'q25',
        question: 'When should you use AI-powered metrics in DSPy?',
        choices: [
          "Never, they're unreliable",
          'For all evaluations',
          'For complex judgments like quality or appropriateness',
          'Only for classification tasks',
        ],
        correctAnswer: 2,
        explanation:
          "AI-powered metrics excel at nuanced judgments that simple rules can't capture.",
      },
      {
        id: 'q26',
        question: "What's a key benefit of using multiple metrics together?",
        choices: [
          'It makes evaluation faster',
          'It provides a more complete picture of performance',
          "It's required by DSPy",
          'It reduces the need for data',
        ],
        correctAnswer: 1,
        explanation:
          'Multiple metrics capture different aspects of quality for comprehensive evaluation.',
      },
      {
        id: 'q27',
        question: 'How do you handle metric failures in DSPy evaluation?',
        choices: [
          'Ignore them',
          'Stop the evaluation',
          'Use error_on_exception parameter',
          'Retry automatically',
        ],
        correctAnswer: 2,
        explanation:
          'The error_on_exception parameter controls whether metric errors stop evaluation.',
      },
      {
        id: 'q28',
        question: "What's the purpose of confidence thresholds in metrics?",
        choices: [
          'To make metrics stricter',
          'To handle uncertainty in judgments',
          'To speed up evaluation',
          'To reduce false positives',
        ],
        correctAnswer: 1,
        explanation:
          "Confidence thresholds help handle cases where the correct answer isn't clear-cut.",
      },
      {
        id: 'q29',
        question: 'Which metric is best for evaluating factual accuracy?',
        choices: ['BLEU score', 'Semantic similarity', 'Exact match or entailment', 'Perplexity'],
        correctAnswer: 2,
        explanation: 'Exact match or entailment-based metrics best capture factual correctness.',
      },
      {
        id: 'q30',
        question: 'How should you evaluate multi-step reasoning in DSPy?',
        choices: [
          'Only check the final answer',
          'Evaluate each step independently',
          'Use a single holistic metric',
          'Skip intermediate steps',
        ],
        correctAnswer: 1,
        explanation: 'Evaluating each step helps identify where reasoning breaks down.',
      },
      {
        id: 'q31',
        question: "What's the trade-off with very strict metrics?",
        choices: [
          "They're more expensive",
          'They may reject valid variations',
          "They're harder to implement",
          'They require more data',
        ],
        correctAnswer: 1,
        explanation:
          'Overly strict metrics might penalize correct but differently-phrased answers.',
      },
      {
        id: 'q32',
        question: 'When is F1 score more useful than accuracy?',
        choices: [
          'For balanced datasets',
          'For imbalanced classification tasks',
          'For generation tasks',
          'Never',
        ],
        correctAnswer: 1,
        explanation: 'F1 score balances precision and recall, useful for imbalanced classes.',
      },
      {
        id: 'q33',
        question: 'How do you implement custom metrics in DSPy?',
        choices: [
          'Subclass dspy.Metric',
          'Write a function taking Example and prediction',
          'Use only built-in metrics',
          'Modify the Evaluate class',
        ],
        correctAnswer: 1,
        explanation:
          'Custom metrics are functions that take an Example and prediction, return boolean.',
      },
      {
        id: 'q34',
        question: "What's the purpose of metric aggregation in evaluation?",
        choices: [
          'To hide individual failures',
          'To provide summary statistics',
          'To speed up computation',
          'To reduce memory usage',
        ],
        correctAnswer: 1,
        explanation: 'Aggregation provides overall performance measures across the dataset.',
      },
      {
        id: 'q35',
        question: 'How should you handle ties in ranking metrics?',
        choices: [
          'Always pick the first',
          'Use consistent tie-breaking rules',
          'Randomize the choice',
          'Reject tied examples',
        ],
        correctAnswer: 1,
        explanation: 'Consistent tie-breaking ensures reproducible evaluation results.',
      },
      {
        id: 'q36',
        question: "What's a good practice for metric validation?",
        choices: [
          'Trust them blindly',
          'Test on known good/bad examples',
          'Only use simple metrics',
          'Avoid custom metrics',
        ],
        correctAnswer: 1,
        explanation: 'Testing metrics on known examples ensures they measure what you intend.',
      },
      {
        id: 'q37',
        question: 'When should metrics consider partial credit?',
        choices: [
          'Never',
          'Always',
          'For tasks with multiple valid approaches',
          'Only for multiple choice',
        ],
        correctAnswer: 2,
        explanation: 'Partial credit is valuable when there are degrees of correctness.',
      },
      {
        id: 'q38',
        question: 'How do you measure efficiency in DSPy programs?',
        choices: [
          'Only accuracy matters',
          'Track tokens, time, and API calls',
          'Count the number of modules',
          'Measure code lines',
        ],
        correctAnswer: 1,
        explanation:
          'Efficiency metrics should track computational resources like tokens and time.',
      },
      {
        id: 'q39',
        question: "What's the danger of overfitting to metrics?",
        choices: [
          'Programs become slower',
          'Programs may game the metric without true improvement',
          'Metrics become invalid',
          'Training fails',
        ],
        correctAnswer: 1,
        explanation:
          'Overfitting to metrics can lead to programs that score well but fail in practice.',
      },
      {
        id: 'q40',
        question: 'How often should you update evaluation metrics?',
        choices: [
          'Never change them',
          'Every evaluation run',
          'When you identify gaps or new requirements',
          'Only when accuracy drops',
        ],
        correctAnswer: 2,
        explanation:
          "Update metrics when you discover they don't capture important quality aspects.",
      },

      // Evaluation Process (Questions 41-60)
      {
        id: 'q41',
        question: "What's the first step in evaluation-driven development?",
        choices: [
          'Write the code',
          'Define success metrics',
          'Collect training data',
          'Choose a model',
        ],
        correctAnswer: 1,
        explanation: 'Start by defining what success looks like through clear metrics.',
      },
      {
        id: 'q42',
        question: 'How do you identify failure patterns in DSPy evaluation?',
        choices: [
          'Look at overall accuracy only',
          'Group and analyze failed examples',
          'Ignore failures',
          'Only fix the worst failures',
        ],
        correctAnswer: 1,
        explanation: 'Grouping failures helps identify systematic issues to address.',
      },
      {
        id: 'q43',
        question: "What's the purpose of stratified evaluation?",
        choices: [
          'To make evaluation faster',
          'To understand performance across categories',
          'To reduce the test set size',
          'To simplify metrics',
        ],
        correctAnswer: 1,
        explanation:
          'Stratified evaluation reveals how performance varies across different segments.',
      },
      {
        id: 'q44',
        question: 'When should you use cross-validation in DSPy?',
        choices: ['Always', 'Never', 'When you have limited data', 'Only for final evaluation'],
        correctAnswer: 2,
        explanation: 'Cross-validation helps maximize limited data for reliable evaluation.',
      },
      {
        id: 'q45',
        question: "What's a sign that your evaluation set is too small?",
        choices: [
          'Consistent results',
          'High variance between runs',
          'Fast evaluation',
          'Perfect scores',
        ],
        correctAnswer: 1,
        explanation:
          "High variance suggests the evaluation set isn't large enough for stable results.",
      },
      {
        id: 'q46',
        question: 'How do you handle versioning in evaluation?',
        choices: [
          "Don't track versions",
          'Track code, data, and metric versions together',
          'Only track code changes',
          'Restart numbering each time',
        ],
        correctAnswer: 1,
        explanation: 'Comprehensive versioning ensures reproducible evaluation results.',
      },
      {
        id: 'q47',
        question: "What's the benefit of continuous evaluation?",
        choices: [
          "It's required by DSPy",
          'Catches regressions early',
          'Makes development slower',
          'Reduces the need for testing',
        ],
        correctAnswer: 1,
        explanation: 'Continuous evaluation quickly identifies when changes hurt performance.',
      },
      {
        id: 'q48',
        question: 'How should you handle edge cases in evaluation?',
        choices: [
          'Exclude them',
          'Include and track separately',
          'Only test on edge cases',
          'Weight them higher',
        ],
        correctAnswer: 1,
        explanation:
          'Track edge cases separately to ensure robustness without skewing overall metrics.',
      },
      {
        id: 'q49',
        question: "What's the purpose of baseline comparisons?",
        choices: [
          'To make results look better',
          'To measure relative improvement',
          'To slow down development',
          "They're not useful",
        ],
        correctAnswer: 1,
        explanation: 'Baselines provide context for whether changes actually improve performance.',
      },
      {
        id: 'q50',
        question: 'When should you do ablation studies in DSPy?',
        choices: [
          'Never',
          'To understand component contributions',
          'Only for published research',
          'Before starting development',
        ],
        correctAnswer: 1,
        explanation: 'Ablation studies reveal which components actually improve performance.',
      },
      {
        id: 'q51',
        question: 'How do you ensure evaluation fairness?',
        choices: [
          'Use the same test set for all systems',
          'Give each system different data',
          'Only evaluate on easy examples',
          'Skip statistical testing',
        ],
        correctAnswer: 0,
        explanation: 'Fair comparison requires evaluating all systems on identical data.',
      },
      {
        id: 'q52',
        question: "What's the risk of evaluation data leakage?",
        choices: [
          'Slower evaluation',
          'Overestimated performance',
          'Underestimated performance',
          'No risk',
        ],
        correctAnswer: 1,
        explanation: "Data leakage leads to overly optimistic results that don't generalize.",
      },
      {
        id: 'q53',
        question: 'How should you report evaluation uncertainty?',
        choices: [
          "Don't report it",
          'Use confidence intervals or error bars',
          'Only report the mean',
          'Round to nice numbers',
        ],
        correctAnswer: 1,
        explanation: 'Confidence intervals communicate the reliability of results.',
      },
      {
        id: 'q54',
        question: "What's the purpose of holdout test sets?",
        choices: [
          'To have more data',
          'To get final unbiased performance estimates',
          'To make evaluation harder',
          "They're not necessary",
        ],
        correctAnswer: 1,
        explanation: 'Holdout sets provide unbiased estimates after all development is done.',
      },
      {
        id: 'q55',
        question: 'When should you re-evaluate old models?',
        choices: ['Never', 'When data distribution shifts', 'Every day', 'Only if they fail'],
        correctAnswer: 1,
        explanation: 'Re-evaluate when your data or requirements change significantly.',
      },
      {
        id: 'q56',
        question: 'How do you handle subjective evaluation tasks?',
        choices: [
          'Avoid them',
          'Use multiple annotators or AI judges',
          'Pick randomly',
          'Use only objective metrics',
        ],
        correctAnswer: 1,
        explanation: 'Multiple judges help handle subjectivity through agreement metrics.',
      },
      {
        id: 'q57',
        question: "What's a good practice for evaluation debugging?",
        choices: [
          'Never look at individual examples',
          'Inspect both successes and failures',
          'Only fix failures',
          'Trust aggregate metrics only',
        ],
        correctAnswer: 1,
        explanation: 'Examining both helps understand what makes predictions succeed or fail.',
      },
      {
        id: 'q58',
        question: 'How do you scale evaluation for large datasets?',
        choices: [
          'Only evaluate on small samples',
          'Use batching and parallelization',
          'Reduce metric complexity',
          'Skip evaluation',
        ],
        correctAnswer: 1,
        explanation: 'Batching and parallel processing maintain thorough evaluation at scale.',
      },
      {
        id: 'q59',
        question: "What's the value of evaluation dashboards?",
        choices: [
          'They look nice',
          'Track performance trends over time',
          "They're required",
          'Replace detailed analysis',
        ],
        correctAnswer: 1,
        explanation: 'Dashboards help visualize performance trends and catch regressions.',
      },
      {
        id: 'q60',
        question: 'When is A/B testing useful for DSPy programs?',
        choices: [
          'Never',
          'For production deployment decisions',
          'During development only',
          'Only for UI changes',
        ],
        correctAnswer: 1,
        explanation: 'A/B testing validates improvements in real-world production settings.',
      },

      // Advanced Topics (Questions 61-80)
      {
        id: 'q61',
        question: 'How does DSPy evaluation differ from traditional ML evaluation?',
        choices: [
          "It's exactly the same",
          'Focus on prompt/program quality vs model training',
          "It's simpler",
          "It doesn't use metrics",
        ],
        correctAnswer: 1,
        explanation: 'DSPy evaluates program design and optimization, not model training.',
      },
      {
        id: 'q62',
        question: "What's the role of bootstrapping in DSPy evaluation?",
        choices: [
          'To create more test data',
          'To estimate metric confidence intervals',
          'To speed up evaluation',
          'To replace cross-validation',
        ],
        correctAnswer: 1,
        explanation: 'Bootstrapping helps estimate the statistical reliability of metrics.',
      },
      {
        id: 'q63',
        question: 'How do you evaluate conversational systems in DSPy?',
        choices: [
          'Only check final responses',
          'Evaluate turn-by-turn and overall dialogue quality',
          'Use the same metrics as QA',
          'Manual evaluation only',
        ],
        correctAnswer: 1,
        explanation: 'Conversational systems need both turn-level and dialogue-level evaluation.',
      },
      {
        id: 'q64',
        question: "What's the importance of calibration in DSPy metrics?",
        choices: [
          "It's not important",
          'Ensures metrics align with human judgment',
          'Makes evaluation faster',
          'Reduces metric complexity',
        ],
        correctAnswer: 1,
        explanation: 'Calibrated metrics better reflect real-world quality assessments.',
      },
      {
        id: 'q65',
        question: 'How do you handle multi-lingual evaluation?',
        choices: [
          'Evaluate each language separately',
          'Only evaluate in English',
          'Use machine translation first',
          'Skip non-English examples',
        ],
        correctAnswer: 0,
        explanation: 'Each language should be evaluated with language-appropriate metrics.',
      },
      {
        id: 'q66',
        question: "What's the benefit of error analysis templates?",
        choices: [
          "They're required by DSPy",
          'Systematic categorization of failure modes',
          'Make evaluation slower',
          'Replace metrics',
        ],
        correctAnswer: 1,
        explanation: 'Templates ensure consistent and thorough error analysis across evaluations.',
      },
      {
        id: 'q67',
        question: 'How do you evaluate robustness in DSPy programs?',
        choices: [
          'Only test on clean data',
          'Test with perturbations and edge cases',
          "Robustness doesn't matter",
          'Use the same test set',
        ],
        correctAnswer: 1,
        explanation: 'Robustness evaluation includes adversarial and edge case testing.',
      },
      {
        id: 'q68',
        question: "What's the purpose of meta-evaluation?",
        choices: [
          'To evaluate the evaluation',
          'To make things complex',
          "It's not useful",
          'To replace regular evaluation',
        ],
        correctAnswer: 0,
        explanation: 'Meta-evaluation ensures your evaluation methods are themselves reliable.',
      },
      {
        id: 'q69',
        question: 'How do you handle temporal evaluation?',
        choices: [
          'Ignore time',
          'Test on data from different time periods',
          'Only use recent data',
          "Time doesn't matter",
        ],
        correctAnswer: 1,
        explanation: 'Temporal evaluation ensures programs work across time periods.',
      },
      {
        id: 'q70',
        question: "What's the value of ensemble evaluation strategies?",
        choices: [
          "They're always worse",
          'Can provide more robust assessments',
          'Too complex to use',
          'Only for research',
        ],
        correctAnswer: 1,
        explanation: 'Ensemble strategies can provide more reliable overall assessments.',
      },
      {
        id: 'q71',
        question: 'How do you evaluate compositional programs in DSPy?',
        choices: [
          'Only evaluate the final output',
          'Evaluate components and their integration',
          'Skip component evaluation',
          'Use single metrics',
        ],
        correctAnswer: 1,
        explanation: 'Compositional evaluation helps identify weak links in the chain.',
      },
      {
        id: 'q72',
        question: "What's important about evaluation reproducibility?",
        choices: [
          "It's optional",
          'Enables reliable comparison and debugging',
          'Makes evaluation slower',
          'Only matters for papers',
        ],
        correctAnswer: 1,
        explanation: 'Reproducibility is crucial for reliable development and comparison.',
      },
      {
        id: 'q73',
        question: 'How do you evaluate zero-shot vs few-shot performance?',
        choices: [
          "They're the same",
          'Test with and without examples separately',
          'Only evaluate few-shot',
          "Zero-shot doesn't work",
        ],
        correctAnswer: 1,
        explanation: 'Separate evaluation reveals when examples are truly needed.',
      },
      {
        id: 'q74',
        question: "What's the role of human evaluation in DSPy?",
        choices: [
          "It's outdated",
          'Validates automatic metrics and catches gaps',
          'Too expensive to use',
          'Always required',
        ],
        correctAnswer: 1,
        explanation: 'Human evaluation validates automatic metrics and finds quality gaps.',
      },
      {
        id: 'q75',
        question: 'How do you handle evaluation with limited compute?',
        choices: [
          'Skip evaluation',
          'Use sampling and prioritization strategies',
          'Only evaluate once',
          'Use simpler programs',
        ],
        correctAnswer: 1,
        explanation: 'Smart sampling can provide good coverage with limited resources.',
      },
      {
        id: 'q76',
        question: "What's the importance of evaluation baselines?",
        choices: [
          "They're optional",
          'Provide context for improvement claims',
          'Make evaluation harder',
          'Only for competitions',
        ],
        correctAnswer: 1,
        explanation: 'Baselines ground performance claims in meaningful comparisons.',
      },
      {
        id: 'q77',
        question: 'How do you evaluate online learning in DSPy?',
        choices: [
          'Same as batch evaluation',
          'Track performance over time windows',
          "Don't evaluate online systems",
          'Only final performance matters',
        ],
        correctAnswer: 1,
        explanation: 'Online evaluation tracks how performance evolves with new data.',
      },
      {
        id: 'q78',
        question: "What's the value of evaluation artifacts?",
        choices: [
          'They waste storage',
          'Enable debugging and analysis',
          'Should be deleted',
          'Only for documentation',
        ],
        correctAnswer: 1,
        explanation: 'Artifacts like predictions and traces enable deep debugging.',
      },
      {
        id: 'q79',
        question: 'How do you balance evaluation thoroughness and speed?',
        choices: [
          'Always prioritize speed',
          'Use hierarchical evaluation strategies',
          'Always be thorough',
          "Speed doesn't matter",
        ],
        correctAnswer: 1,
        explanation: 'Hierarchical strategies do quick checks first, deep dives when needed.',
      },
      {
        id: 'q80',
        question: "What's the future of evaluation in DSPy?",
        choices: [
          "It won't change",
          'More automated, adaptive, and integrated',
          'Less important over time',
          'Completely manual',
        ],
        correctAnswer: 1,
        explanation: 'Evaluation will become more automated and integrated into development.',
      },

      // Best Practices and Common Pitfalls (Questions 81-100)
      {
        id: 'q81',
        question: "What's the most common evaluation mistake in DSPy?",
        choices: [
          'Evaluating too often',
          'Using only accuracy metrics',
          'Having too many metrics',
          'Evaluating on large datasets',
        ],
        correctAnswer: 1,
        explanation: 'Over-relying on accuracy misses other important quality dimensions.',
      },
      {
        id: 'q82',
        question: 'How do you avoid overfitting during optimization?',
        choices: [
          "Don't optimize",
          'Use separate train and validation sets',
          'Only optimize once',
          'Use more data',
        ],
        correctAnswer: 1,
        explanation: 'Separate validation sets prevent overfitting to training data.',
      },
      {
        id: 'q83',
        question: "What's a sign of a poorly designed metric?",
        choices: [
          "It's fast to compute",
          'High scores but poor real-world performance',
          "It's simple",
          'It uses AI',
        ],
        correctAnswer: 1,
        explanation: "Metrics that don't correlate with real quality are poorly designed.",
      },
      {
        id: 'q84',
        question: 'When should you NOT use semantic similarity?',
        choices: [
          'For paraphrase detection',
          'For exact factual information',
          'For general QA',
          'For summarization',
        ],
        correctAnswer: 1,
        explanation: 'Exact facts need exact match or entailment, not just similarity.',
      },
      {
        id: 'q85',
        question: 'How do you handle evaluation of creative tasks?',
        choices: [
          'Only use subjective judgment',
          'Combine multiple metrics including AI judges',
          'Skip evaluation',
          'Use exact match',
        ],
        correctAnswer: 1,
        explanation: 'Creative tasks benefit from multiple perspectives on quality.',
      },
      {
        id: 'q86',
        question: "What's the danger of evaluation hacking?",
        choices: [
          'It improves performance',
          'Optimizing metrics without real improvement',
          "It's encouraged",
          'Makes evaluation faster',
        ],
        correctAnswer: 1,
        explanation: 'Gaming metrics leads to systems that fail in real use.',
      },
      {
        id: 'q87',
        question: 'How frequently should you run full evaluation?',
        choices: [
          'Every code change',
          'At major milestones and before deployment',
          'Once per year',
          'Never',
        ],
        correctAnswer: 1,
        explanation: 'Balance evaluation cost with the need to catch regressions.',
      },
      {
        id: 'q88',
        question: "What's the best practice for metric documentation?",
        choices: [
          "Don't document metrics",
          'Clear definitions with examples',
          'Just the formula',
          'Reference external docs',
        ],
        correctAnswer: 1,
        explanation: 'Good documentation ensures consistent metric understanding.',
      },
      {
        id: 'q89',
        question: 'How do you handle metric conflicts?',
        choices: [
          'Ignore them',
          'Understand trade-offs and prioritize',
          'Average all metrics',
          'Only use one metric',
        ],
        correctAnswer: 1,
        explanation: 'Different metrics capture different qualities; understand trade-offs.',
      },
      {
        id: 'q90',
        question: "What's essential for production evaluation?",
        choices: [
          'Academic metrics only',
          'Real-world performance indicators',
          'Perfect accuracy',
          'Complex metrics',
        ],
        correctAnswer: 1,
        explanation: 'Production evaluation must reflect actual user needs.',
      },
      {
        id: 'q91',
        question: 'How do you validate new evaluation approaches?',
        choices: [
          'Trust them immediately',
          'Compare with established methods and human judgment',
          'Never try new approaches',
          'Use them everywhere',
        ],
        correctAnswer: 1,
        explanation: 'New approaches need validation against known-good methods.',
      },
      {
        id: 'q92',
        question: "What's the role of negative examples in evaluation?",
        choices: [
          "They're not useful",
          'Help ensure systems can reject bad inputs',
          'Make evaluation harder',
          'Should be avoided',
        ],
        correctAnswer: 1,
        explanation: 'Negative examples test whether systems appropriately handle bad inputs.',
      },
      {
        id: 'q93',
        question: 'How do you communicate evaluation results to stakeholders?',
        choices: [
          'Show all raw numbers',
          'Summarize key findings with context',
          'Hide negative results',
          'Use technical jargon',
        ],
        correctAnswer: 1,
        explanation: 'Stakeholders need clear summaries with actionable insights.',
      },
      {
        id: 'q94',
        question: "What's the benefit of evaluation checkpoints?",
        choices: [
          'They slow development',
          'Enable rollback and comparison',
          'Not useful',
          'Only for version control',
        ],
        correctAnswer: 1,
        explanation: 'Checkpoints allow comparison and recovery of good versions.',
      },
      {
        id: 'q95',
        question: 'How do you handle evaluation of safety-critical systems?',
        choices: [
          'Same as any system',
          'More rigorous testing with failure analysis',
          'Skip evaluation',
          'Only test happy paths',
        ],
        correctAnswer: 1,
        explanation: 'Safety-critical systems need extensive evaluation of failure modes.',
      },
      {
        id: 'q96',
        question: "What's the importance of evaluation automation?",
        choices: [
          "It's optional",
          'Enables consistent, frequent evaluation',
          'Makes evaluation worse',
          'Only for large teams',
        ],
        correctAnswer: 1,
        explanation: 'Automation ensures consistent evaluation without manual overhead.',
      },
      {
        id: 'q97',
        question: 'How do you evaluate explanation quality in DSPy?',
        choices: [
          'Only check if explanation exists',
          'Assess completeness, accuracy, and clarity',
          'Length is all that matters',
          'Skip explanation evaluation',
        ],
        correctAnswer: 1,
        explanation: 'Good explanations need multiple quality dimensions assessed.',
      },
      {
        id: 'q98',
        question: "What's a key principle of iterative evaluation?",
        choices: [
          'Evaluate once at the end',
          'Regular evaluation guides development',
          'Evaluation slows iteration',
          'Only evaluate successes',
        ],
        correctAnswer: 1,
        explanation: 'Regular evaluation provides feedback for continuous improvement.',
      },
      {
        id: 'q99',
        question: 'How do you ensure evaluation covers edge cases?',
        choices: [
          "Hope they're included",
          'Explicitly design test cases for edges',
          'Ignore edge cases',
          'Only test normal cases',
        ],
        correctAnswer: 1,
        explanation: 'Deliberate edge case design ensures robust evaluation.',
      },
      {
        id: 'q100',
        question: "What's the ultimate goal of evaluation in DSPy?",
        choices: [
          'Get high scores',
          'Build reliable, effective AI systems',
          'Publish papers',
          'Minimize evaluation time',
        ],
        correctAnswer: 1,
        explanation: 'Evaluation enables building AI systems that work well in practice.',
      },
    ],
  },
};
